<template>
  <div class="order-groups2-page" :aria-busy="pageBootstrapState2 !== 'ready' ? 'true' : 'false'">
    <CAlert v-if="draft && draft.builder && (draft.builder.blank_recovery || []).length" color="warning" id="blank_recovery2">
      <strong>Imported group review</strong>
      <p>The legacy mixed group was separated to keep blank and decorated merchandise distinct. Assign each original value through the appropriate group controls, or explicitly exclude it, then confirm review. Values have not been copied to both groups.</p>
      <div v-for="row in draft.builder.blank_recovery" :key="row.id">
        <strong>{{ row.field }}</strong> · Legacy group {{ row.source_group_id }}
        <p>{{ blankRecoveryValue2(row.value) }}</p>
        <CButton v-if="row.status !== 'resolved'" color="warning" @click="resolveBlankRecovery2(row)">Confirm Value Reviewed</CButton>
        <span v-else>Reviewed</span>
      </div>
    </CAlert>

    <PageBootstrap2
      v-if="pageBootstrapState2 !== 'ready'"
      :state="pageBootstrapState2"
      :message="pageBootstrapMessage2"
      :reference="pageBootstrapReference2"
      :allow-return="pageBootstrapState2 === 'failed'"
      @retry="initializeQuoteFlow2"
      @return-builder="goBackToBuilder2"
    />
    <template v-else>
    <CAlert color="danger" show v-if="draftPersistenceWarning2">{{ draftPersistenceWarning2 }}</CAlert>
    <CAlert color="info" show v-if="shipDateUpdating2">Ship dates are being recalculated.</CAlert>
    <CAlert color="danger" show v-if="shipDateError2">{{ shipDateError2 }}</CAlert>
    <details v-if="placementDiagnosticsEnabled2()" class="temporary-storage-diagnostics mb-3">
      <summary><strong>V2 storage and date diagnostics</strong></summary>
      <div>Draft schema version: 1</div>
      <div>Last persistence: {{ draftPersistenceResult2 ? (draftPersistenceResult2.ok ? 'verified' : (draftPersistenceResult2.quotaExceeded ? 'quota exceeded' : 'failed')) : 'not attempted' }}</div>
      <div v-for="entry in storageInventory2" :key="entry.key">{{ entry.key }}: {{ entry.bytes }} bytes</div>
      <div>Ship-date request generation: {{ shipDateRequestGeneration2 }}</div>
      <div v-for="group in (draft && draft.groups || [])" :key="'date_diag_' + group.id">{{ group.id }}: {{ group.ship_date || 'pending' }} · {{ group.ship_date_constraint ? group.ship_date_constraint.type + ' ' + group.ship_date_constraint.days : 'no determining constraint' }}</div>
      <div>Cached/request activity: {{ requestDiagnostics2().length }}</div>
    </details>
    <div class="start-order-actions">
      <CButton color="outline-danger" @click="startNewOrder2">Start New Order</CButton>
    </div>

    <CAlert color="warning" show v-if="missingDraft">
      No v2 builder draft was found for group review. Return to create order v2 and continue from there.
    </CAlert>

    <CModal class="pdf-review-modal2" add-content-classes="pdf-review-modal-content2" :show.sync="pdfReview2.open" title="Review Order PDFs" size="xl" :close-on-backdrop="!submitInProgress" @hidden="restoreReviewSubmitFocus2">
      <div v-if="reviewCurrentGroup2()" class="pdf-review-shell2">
        <div class="pdf-review-header2">
          <div>
            <strong>Group {{ pdfReview2.index + 1 }} of {{ (draft.groups || []).length }}</strong>
            <div>{{ reviewGroupLabel2(reviewCurrentGroup2()) }}</div>
          </div>
          <div aria-live="polite">{{ reviewedGroupCount2() }} of {{ (draft.groups || []).length }} groups reviewed</div>
        </div>
        <div class="pdf-review-tabs2" role="tablist" aria-label="Order PDF groups">
          <button v-for="(reviewGroup, reviewIndex) in (draft.groups || [])" :key="'review_tab_' + reviewGroup.id" type="button" role="tab" :aria-selected="pdfReview2.index === reviewIndex ? 'true' : 'false'" :class="{ active: pdfReview2.index === reviewIndex, reviewed: groupReviewed2(reviewGroup) }" @click="showReviewGroup2(reviewIndex)">
            {{ reviewIndex + 1 }}<span class="sr-only"> Group {{ reviewIndex + 1 }}</span>
          </button>
        </div>
        <div ref="pdfReviewBody2" class="pdf-review-preview2">
          <authoritative-pdf-review
            :key="'final_review_' + reviewCurrentGroup2().id + '_' + pdfReview2.fingerprint"
            :form="previewFormForGroup2(reviewCurrentGroup2())"
            :quote-projection="quoteGroupFor2(reviewCurrentGroup2())"
            :authoritative-v2="true"
            :preview="true"
            :type="'final_group2_' + reviewCurrentGroup2().id"
            :current="!quoteUpdating && !quoteStale && !quoteError && !!quoteResult"
            :diagnostics="placementDiagnosticsEnabled2()"
            @rendered="markReviewGroupRendered2(reviewCurrentGroup2())"
          />
        </div>
      </div>
      <template #footer>
        <CButton color="secondary" @click="closePdfReview2" :disabled="submitInProgress">Close</CButton>
        <CButton color="light" @click="showReviewGroup2(pdfReview2.index - 1)" :disabled="submitInProgress || pdfReview2.index <= 0">Previous Group</CButton>
        <CButton color="info" @click="showReviewGroup2(pdfReview2.index + 1)" :disabled="submitInProgress || !draft || pdfReview2.index >= draft.groups.length - 1">Next Group</CButton>
        <CButton color="success" @click="finalSubmitReviewedOrder2" :disabled="submitInProgress || !allReviewGroupsViewed2()">
          {{ submitInProgress ? 'Submitting...' : 'Submit Order' }}
        </CButton>
      </template>
    </CModal>

    <CRow v-if="!missingDraft && draft">
      <CCol sm="12" lg="6">
        <CCard class="mb-3" id="global_order_information">
          <CCardHeader>Order Delivery</CCardHeader>
          <CCardBody>
            <div class="summary-block mb-3">
              <div><strong>PO Number:</strong> {{ draft.builder.po_number || 'Not entered in Step 1 yet' }}</div>
              <div v-if="draft.builder.wait_on_customer_po"><strong>Customer Requires PO:</strong> {{ draft.builder.po_number ? 'PO supplied' : 'Order will wait for customer PO' }}</div>
            </div>

            <div class="summary-block">
              <div><strong>Sold To:</strong> {{ customerSummary2() }}</div>
              <div><strong>Ship To:</strong> {{ shipToSummary2() }}</div>
              <div v-if="draft.builder.rep"><strong>Rep:</strong> {{ draft.builder.rep.name || draft.builder.rep.rep_number || '' }}</div>
            </div>

            <div class="timing-review2" v-if="activeTimingSummary2">
              <div class="timing-review2-header"><strong>Order Timing</strong><button type="button" class="btn btn-link btn-sm" @click="editOrderTiming2">Edit in Builder</button></div>
              <div v-if="draft.builder.scheduling_intent.event_order"><strong>Event Date:</strong> {{ draft.builder.scheduling_intent.in_hands_date }}</div>
              <EventSchedule2 v-if="eventValidationVisible2" :message="eventMessage2" :authorized="eventAuthorized2" :loading="eventLoading2" :pending="eventAutoPending2 || !eventCurrent2" :failed="!!eventError2 || !!eventCalendarError2" :modal="eventModal2" :result="eventResult2" :group-label="eventGroupLabel2()" @check="refreshEventSchedule2(false)" @move="moveEventDate2" @timing="eventModal2 = false; editOrderTiming2()" @configuration="changeEventConfiguration2" @cancel="eventModal2 = false" />
              <div v-if="draft.builder.scheduling_intent.future_ship_date_boolean"><strong>Do Not Ship Before:</strong> {{ draft.builder.scheduling_intent.future_ship_date }}</div>
              <div v-if="timingFastLaneConfirmed2"><strong>Fast Lane:</strong> Confirmed</div>
              <div v-if="draft.order.prebook"><strong>Prebook:</strong> {{ draft.order.prebook.pre_book_label }}</div>
              <div v-if="prebookCatalogError2" class="helper-text text-warning" role="status">Prebook options could not be loaded. A saved selection must be verified before submission. <button type="button" class="btn btn-link" @click="loadPrebookOptions2">Retry</button></div>
              <div v-if="prebookNotice2" class="helper-text text-warning" role="status">{{ prebookNotice2 }}</div>
            </div>

          </CCardBody>
        </CCard>
      </CCol>

      <CCol sm="12" lg="6">
        <CCard class="mb-3" id="email_delivery">
          <CCardHeader>Email Delivery</CCardHeader>
          <CCardBody>
            <div class="helper-text mb-3">
              Check the email addresses that should receive the order copy. If the row is marked as Ship To Email, editing that address here also updates the shipping contact email on submit.
            </div>
            <div v-if="draft.order.available_emails && draft.order.available_emails.length">
              <div
                v-for="(emailRow, emailIndex) in draft.order.available_emails"
                :key="'email_row_' + emailIndex"
                class="email-row"
              >
                <label class="toggle-row">
                  <input type="checkbox" v-model="emailRow.checked" />
                  <span>{{ emailRow.ship_to_email ? 'Ship To Email' : 'Sold To Email' }}</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  v-model="emailRow.email"
                />
              </div>
            </div>

            <CInput
              label="Additional Emails"
              v-model="draft.order.extra_emails"
              placeholder="comma,separated@example.com"
            />

            <CTextarea
              label="Email Text"
              rows="4"
              v-model="draft.order.email_text"
            />
          </CCardBody>
        </CCard>
      </CCol>

      <CCol sm="12">
        <CCard class="mb-3 authoritative-quote-card" id="pricing_needs_review">
          <CCardHeader>
            <div class="group-header">
              <div>
                <div class="section-kicker">Server pricing</div>
                <div class="group-title">Server Order Quote</div>
              </div>
              <span v-if="quoteUpdating" class="quote-status updating">Updating…</span>
              <span v-else-if="quoteStale" class="quote-status stale">Stale</span>
              <span v-else-if="quoteHasBlockingIssues2()" class="quote-status stale">Pricing needs review</span>
              <span v-else-if="quoteResult && quoteResult.authoritative !== false" class="quote-status current">Current</span>
            </div>
          </CCardHeader>
          <CCardBody>
            <div v-if="quoteResult">
              <div class="quoted-order-total" v-if="!quoteHasBlockingIssues2()">
                <span>Quoted order total</span>
                <strong>{{ orderTotalDisplay2() }}</strong>
              </div>
              <div v-else class="quote-error">
                <strong>Pricing needs review.</strong>
                <div class="helper-text">A final authoritative total is unavailable until required decoration pricing is resolved.</div>
                <div v-if="quoteResult.totals.provisional_order_total !== undefined" class="mt-2">
                  Provisional resolved components: {{ displayCurrencyOrUnavailable2(quoteResult.totals.provisional_order_total) }}
                </div>
                <ul class="mt-2 mb-0">
                  <li v-for="(issue, issueIndex) in quoteBlockingReasons2()" :key="'quote_issue_' + issueIndex">
                    {{ issue.message }}
                  </li>
                </ul>
              </div>
              <div v-if="quoteError" class="quote-error mt-3">
                {{ quoteError }}
                <CButton size="sm" color="outline-danger" @click="retryQuote2">Retry</CButton>
              </div>
            </div>
            <div v-else-if="quoteUpdating" class="helper-text">Requesting the authoritative quote…</div>
            <div v-else class="quote-error">
              Authoritative pricing is unavailable.
              <span v-if="quoteError">{{ quoteError }}</span>
              <CButton size="sm" color="outline-danger" @click="retryQuote2">Retry</CButton>
            </div>
          </CCardBody>
        </CCard>

        <CCard class="mb-3 group-navigator-card">
          <CCardHeader>Order Groups</CCardHeader>
          <CCardBody>
            <div class="group-navigator" role="tablist" aria-label="Order Groups">
              <div
                v-for="(group, groupIndex) in draft.groups"
                :key="'group_nav_' + group.id"
                role="tab"
                tabindex="0"
                class="group-navigator-item"
                :class="{ active: group.id === activeGroupId2, ready: groupReady2(group), incomplete: !groupReady2(group) }"
                :aria-selected="group.id === activeGroupId2 ? 'true' : 'false'"
                @click="selectGroup2(group, groupIndex)"
                @keydown.enter.prevent="selectGroup2(group, groupIndex)"
                @keydown.space.prevent="selectGroup2(group, groupIndex)"
              >
                <span class="group-navigator-number">Order Group {{ groupIndex + 1 }}</span>
                <strong>{{ group.program_info ? group.program_info.program_name : 'Program needed' }}</strong>
                <span class="group-navigator-status">{{ groupReady2(group) ? 'Ready' : 'Needs Attention' }}</span>
                <span class="group-navigator-chips">
                  <span v-for="(chip, chipIndex) in groupStyleChips2(group)" :key="group.id + '_nav_style_' + chipIndex" class="group-nav-chip">{{ chip }}</span>
                </span>
                <span class="group-navigator-chips">
                  <span v-for="(chip, chipIndex) in groupDecorationChips2(group)" :key="group.id + '_nav_dec_' + chipIndex" class="group-nav-chip decoration">{{ chip }}</span>
                </span>
                <span class="group-navigator-counts">
                  {{ countLabel2(group.items.length, 'style') }} · {{ countLabel2(group.total_units, 'unit') }} · {{ countLabel2(groupDecorations2(group).length, 'decoration') }}
                </span>
              </div>
            </div>
          </CCardBody>
        </CCard>

        <div class="active-group-workspace">
            <CCard
              v-for="(group, groupIndex) in draft.groups"
              v-if="group.id === activeGroupId2"
              :key="group.id"
              class="mb-3 group-card"
              :id="'group_attention_' + group.id"
            >
              <CCardHeader>
                <div class="group-header">
                  <div>
                    <div class="group-title">Order Group {{ groupIndex + 1 }}</div>
                    <div class="group-subtitle">
                      <span v-if="groupDisplayName2(group, groupIndex)">{{ groupDisplayName2(group, groupIndex) }} • </span>{{ group.program_info ? group.program_info.program_name : '' }} • {{ countLabel2(group.total_units, 'unit') }} • {{ countLabel2(group.items.length, 'style') }}
                    </div>
                  </div>
                  <span class="group-navigator-status" :class="groupReady2(group) ? 'ready' : 'incomplete'">{{ groupReady2(group) ? 'Ready' : 'Needs Attention' }}</span>
                </div>
              </CCardHeader>
              <CCardBody>
                <promotion-popup
                  v-if="group.showPromoPopup"
                  :visible.sync="group.showPromoPopup"
                  :group="group"
                  :onClose="() => group.showPromoPopup = false"
                  :onApply="(promotion) => applyPromotion2(group, promotion)"
                />

                <div class="summary-block mb-3">
                  <div class="detail-heading">Styles in Order Group {{ groupIndex + 1 }}</div>
                  <div class="tag-row">
                    <span
                      v-for="item in group.items"
                      :key="group.id + '_item_' + item.style_id"
                      class="summary-chip"
                    >
                      {{ item.style_name }}: {{ item.units }}
                    </span>
                  </div>
                </div>

                <CCard v-if="placementDiagnosticsEnabled2()" class="mb-3 placement-diagnostic-card">
                  <CCardHeader class="group-collapsible-header" role="button" tabindex="0" :aria-expanded="placementDiagnosticsOpen2(group) ? 'true' : 'false'" @click="togglePlacementDiagnostics2(group)" @keydown.enter.prevent="togglePlacementDiagnostics2(group)" @keydown.space.prevent="togglePlacementDiagnostics2(group)">
                    <div><strong>TEMPORARY DIAGNOSTIC · Temporary Placement Diagnostics</strong><span class="group-panel-summary">Display-only builder → pricing/PDF/submission verification</span></div>
                    <CButton size="sm" color="light" @click.stop="togglePlacementDiagnostics2(group)">{{ placementDiagnosticsOpen2(group) ? 'Collapse' : 'Expand' }}</CButton>
                  </CCardHeader>
                  <CCollapse :show="placementDiagnosticsOpen2(group)">
                    <CCardBody class="placement-diagnostic-body">
                      <div v-if="validProductClassSpecificGroup2(group, groupIndex)" class="text-success mb-2"><strong>Status: Valid product-class-specific resolution</strong></div>
                      <div v-for="row in placementDiagnosticRows2(group, groupIndex)" :key="'placement_diag_' + group.id + '_' + row.sectionId + '_' + row.styleId" class="placement-diagnostic-item">
                        <div class="placement-diagnostic-heading">Order Group {{ row.groupNumber }} / {{ diagnosticValue2(row.groupId) }} · Section {{ diagnosticValue2(row.sectionId) }}</div>
                        <div>Style: {{ diagnosticValue2(row.styleNumber || row.styleId) }} · {{ diagnosticValue2(row.styleDescription) }}</div>
                        <div>Program: {{ diagnosticValue2(row.programName) }} ({{ diagnosticValue2(row.programId) }})</div>
                        <div>Target Style product_class: {{ diagnosticValue2(row.targetProductClass) }}</div>
                        <div>Exact Section-generation/grouping key: {{ diagnosticValue2(row.groupingKey) }}</div>
                        <div v-for="decoration in row.decorations" :key="'placement_diag_dec_' + row.styleId + '_' + decoration.index" class="placement-diagnostic-decoration" :class="{ 'diagnostic-mismatch': decoration.status !== 'Resolved' }">
                          <strong>Decoration {{ decoration.index + 1 }} · {{ decoration.status }}</strong>
                          <div class="placement-diagnostic-grid">
                            <div><b>Design/source</b><br>Artwork ID/tape: {{ diagnosticValue2(decoration.source && decoration.source.artwork) }}<br>Technique: {{ diagnosticValue2(decoration.source && decoration.source.techniqueId) }} / internal {{ diagnosticValue2(decoration.source && decoration.source.techniqueInternalId) }}<br>Location: {{ diagnosticValue2(decoration.source && decoration.source.locationCode) }} / ID {{ diagnosticValue2(decoration.source && decoration.source.locationId) }} / internal {{ diagnosticValue2(decoration.source && decoration.source.locationInternalId) }}</div>
                            <div><b>Builder resolved value</b><br>Technique: {{ diagnosticValue2(decoration.builder && decoration.builder.techniqueId) }} / internal {{ diagnosticValue2(decoration.builder && decoration.builder.techniqueInternalId) }}<br>Location: {{ diagnosticValue2(decoration.builder && decoration.builder.locationCode) }} / ID {{ diagnosticValue2(decoration.builder && decoration.builder.locationId) }} / internal {{ diagnosticValue2(decoration.builder && decoration.builder.locationInternalId) }}<br>Target product_class: {{ diagnosticValue2(decoration.builder && decoration.builder.productClass) }}<br>PTL/configuration: {{ diagnosticValue2(decoration.builder && (decoration.builder.ptlIdentity || decoration.builder.ptlId)) }}<br>Resolution source: {{ diagnosticValue2(decoration.builder && decoration.builder.resolutionSource) }}</div>
                            <div><b>Pricing/evaluator request value</b><br>Technique ID: {{ diagnosticValue2(decoration.pricing && decoration.pricing.techniqueId) }} / internal {{ diagnosticValue2(decoration.pricing && decoration.pricing.techniqueInternalId) }}<br>Location ID: {{ diagnosticValue2(decoration.pricing && decoration.pricing.locationId) }} / internal {{ diagnosticValue2(decoration.pricing && decoration.pricing.locationInternalId) }}<br>Product class: {{ diagnosticValue2(decoration.pricing && decoration.pricing.productClass) }}<br>PTL: {{ diagnosticValue2(decoration.pricing && decoration.pricing.ptlIdentity) }}<br>Resolution source: {{ diagnosticValue2(decoration.pricing && decoration.pricing.resolutionSource) }}</div>
                            <div><b>Evaluator result</b><br>Technique ID: {{ diagnosticValue2(decoration.evaluator && decoration.evaluator.techniqueId) }}<br>Location ID: {{ diagnosticValue2(decoration.evaluator && decoration.evaluator.locationId) }}<br>PTL: {{ diagnosticValue2(decoration.evaluator && decoration.evaluator.ptlIdentity) }}<br>Resolution source: {{ diagnosticValue2(decoration.evaluator && decoration.evaluator.resolutionSource) }}<br>Resolution path: {{ diagnosticValue2(decoration.evaluator && decoration.evaluator.evaluatorPath) }}</div>
                            <div><b>PDF payload value</b><br>Technique: {{ diagnosticValue2(decoration.pdf && decoration.pdf.techniqueId) }} / internal {{ diagnosticValue2(decoration.pdf && decoration.pdf.techniqueInternalId) }}<br>Location: {{ diagnosticValue2(decoration.pdf && decoration.pdf.locationCode) }} / ID {{ diagnosticValue2(decoration.pdf && decoration.pdf.locationId) }} / internal {{ diagnosticValue2(decoration.pdf && decoration.pdf.locationInternalId) }}<br>PTL/configuration: {{ diagnosticValue2(decoration.pdf && (decoration.pdf.ptlIdentity || decoration.pdf.ptlId)) }}</div>
                            <div><b>Submission payload value</b><br>Technique: {{ diagnosticValue2(decoration.submission && decoration.submission.techniqueId) }} / internal {{ diagnosticValue2(decoration.submission && decoration.submission.techniqueInternalId) }}<br>Location: {{ diagnosticValue2(decoration.submission && decoration.submission.locationCode) }} / ID {{ diagnosticValue2(decoration.submission && decoration.submission.locationId) }} / internal {{ diagnosticValue2(decoration.submission && decoration.submission.locationInternalId) }}<br>Target product_class: {{ diagnosticValue2(decoration.submission && decoration.submission.productClass) }}<br>PTL/configuration: {{ diagnosticValue2(decoration.submission && (decoration.submission.ptlIdentity || decoration.submission.ptlId)) }}<br>Grouping inputs carried by item: {{ diagnosticValue2(decoration.submission && decoration.submission.locationCode) }} + {{ diagnosticValue2(decoration.submission && decoration.submission.artwork) }}</div>
                          </div>
                        </div>
                      </div>
                    </CCardBody>
                  </CCollapse>
                </CCard>

                <div class="summary-block mb-3" v-if="quoteGroupFor2(group)">
                  <div class="detail-heading">{{ groupPricingHeading2() }}</div>
                  <div class="quote-breakdown">
                    <div><span>{{ pricingComponentLabel2('Merchandise') }}</span><strong>{{ displayCurrencyOrUnavailable2(quoteGroupFor2(group).totals.merchandise_subtotal) }}</strong></div>
                    <div><span>{{ pricingComponentLabel2('Decoration') }}</span><strong>{{ displayCurrencyOrUnavailable2(quoteGroupFor2(group).totals.decoration_subtotal) }}</strong></div>
                    <div class="additional-locations-charge"><span>{{ pricingComponentLabel2('Additional Locations Charge') }}</span><strong>{{ displayCurrencyOrUnavailable2(quoteGroupFor2(group).totals.additional_locations) }}</strong></div>
                    <div><span>{{ pricingComponentLabel2('Under-minimum adjustment') }}</span><strong>{{ displayCurrencyOrUnavailable2(quoteGroupFor2(group).totals.under_minimum) }}</strong></div>
                    <div><span>{{ pricingComponentLabel2('1–23 adjustment') }}</span><strong>{{ displayCurrencyOrUnavailable2(quoteGroupFor2(group).totals.one_to_23_upcharges) }}</strong></div>
                    <div><span>{{ pricingComponentLabel2('Additional charges') }}</span><strong>{{ displayCurrencyOrUnavailable2(quoteGroupFor2(group).totals.additional_charges) }}</strong></div>
                    <div><span>{{ pricingComponentLabel2('Royalties') }}</span><strong>{{ displayCurrencyOrUnavailable2(quoteGroupFor2(group).totals.royalties) }}</strong></div>
                    <div v-if="showGroupPromotionAdjustment2(group)" class="promotion-adjustment"><span>{{ pricingComponentLabel2('Promotion adjustment') }}</span><strong>-{{ formatCurrency2(quoteGroupFor2(group).totals.promotion_adjustment) }}</strong></div>
                    <div class="quote-breakdown-total"><span>{{ quoteHasBlockingIssues2() ? 'Group total' : 'Quoted group total' }}</span><strong>{{ groupTotalDisplay2(group) }}</strong></div>
                  </div>
                </div>

                <CRow>
                  <CCol sm="12" md="6" :id="'group_ship_date_' + group.id">
                    <label class="field-label">Ship Date</label>
                    <div class="form-control calculated-ship-date" aria-readonly="true">{{ group.ship_date || 'Pending configuration' }}</div>
                    <div class="helper-text" v-if="group.ship_date_constraint">Calculated from {{ group.ship_date_constraint.label }} ({{ group.ship_date_constraint.days }} business days).</div>
                    <div class="helper-text" v-else>Ship Date is calculated after Program, Style quantities, and every Decoration configuration are complete.</div>
                  </CCol>
                  <CCol sm="12" md="6" :id="'group_ship_via_' + group.id">
                    <label class="field-label">Ship Via</label>
                    <v-select
                      v-model="group.ship_via"
                      @input="authoritativeQuoteInputChanged2"
                      :options="shipViaOptions"
                      label="ship_via_text"
                    >
                      <template slot="option" slot-scope="option">
                        {{ option.ship_via_code }} - {{ option.ship_via_text }}
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        {{ option.ship_via_code }} - {{ option.ship_via_text }}
                      </template>
                    </v-select>
                  </CCol>
                  <CCol sm="12" md="6">
                    <label class="compact-order-toggle2 mt-4" :class="{ 'is-selected': group.wait_on_customer }">
                      <input type="checkbox" data-test="wait-on-customer" v-model="group.wait_on_customer" />
                      <span>Wait On Customer Approval</span>
                    </label>
                  </CCol>
                </CRow>

                <CTextarea
                  label="Special Instructions"
                  rows="4"
                  maxlength="300"
                  v-model="group.special_instructions"
                  placeholder="What should show on the group PDF or be called out to production?"
                />

                <div class="program-options" v-if="showProgramOptions2(group)">
                  <div class="detail-heading">Program Options</div>
                  <div class="tier-row" v-if="isTierProgram2(group)">
                    <label class="field-label">Empire Tier</label>
                    <select class="form-control" v-model="group.tier" @change="authoritativeQuoteInputChanged2">
                      <option value="">Select Tier</option>
                      <option value="0">Tier 0 (&lt;72 total pieces)</option>
                      <option value="1">Tier 1 (12+)</option>
                      <option value="2">Tier 2 (24+)</option>
                      <option value="3">Tier 3 (36+)</option>
                      <option value="6">Tier 6 (72+)</option>
                    </select>
                    <small class="text-muted d-block mt-1">
                      Preview pricing uses this tier for Empire-style programs.
                    </small>
                  </div>
                  <div class="toggle-grid">
                    <label class="toggle-row" v-if="group.program_info && group.program_info.rush_option">
                      <input type="checkbox" v-model="group.rush" @change="authoritativeQuoteInputChanged2" />
                      <span>Rush</span>
                    </label>
                    <label class="toggle-row" v-if="group.program_info && group.program_info.ribbon_option">
                      <input type="checkbox" v-model="group.ribbon" @change="authoritativeQuoteInputChanged2" />
                      <span>Custom Ribbon</span>
                    </label>
                    <label class="toggle-row" v-if="group.program_info && group.program_info.hangtags_option">
                      <input type="checkbox" v-model="group.hangtags" @change="authoritativeQuoteInputChanged2" />
                      <span>Hangtags</span>
                    </label>
                    <label class="toggle-row" v-if="group.program_info && group.program_info.string_tags_option">
                      <input type="checkbox" v-model="group.string_tags" @change="authoritativeQuoteInputChanged2" />
                      <span>String Tags</span>
                    </label>
                    <div><strong>Fast Lane:</strong> {{ draft.order.fast_lane ? 'Selected for entire order' : 'Not selected' }}</div>
                  </div>
                </div>

                <div class="summary-block mb-3" v-if="group.selected_promotion">
                  <div><strong>Promotion:</strong> {{ group.selected_promotion.name }}</div>
                  <div v-if="group.selected_promotion.description">{{ group.selected_promotion.description }}</div>
                </div>

                <CCard class="mb-3 group-collapsible-card" :id="'group_additional_charges_' + group.id">
                  <CCardHeader class="group-collapsible-header" role="button" tabindex="0" :aria-expanded="groupPanelOpen2(group, 'charges') ? 'true' : 'false'" @click="toggleGroupPanel2(group, 'charges')" @keydown.enter.prevent="toggleGroupPanel2(group, 'charges')" @keydown.space.prevent="toggleGroupPanel2(group, 'charges')">
                    <div><strong>Additional Charges/Discounts</strong><span class="group-panel-summary">{{ additionalChargesSummary2(group) }}</span></div>
                    <CButton size="sm" color="light" @click.stop="toggleGroupPanel2(group, 'charges')">{{ groupPanelOpen2(group, 'charges') ? 'Collapse' : 'Expand' }}</CButton>
                  </CCardHeader>
                  <CCollapse :show="groupPanelOpen2(group, 'charges')">
                    <CCardBody><additional-charges-table :group="group" :embedded="true" @input-changed="authoritativeQuoteInputChanged2" /></CCardBody>
                  </CCollapse>
                </CCard>

                <CCard class="mb-3 group-collapsible-card">
                  <CCardHeader class="group-collapsible-header" role="button" tabindex="0" :aria-expanded="groupPanelOpen2(group, 'royalties') ? 'true' : 'false'" @click="toggleGroupPanel2(group, 'royalties')" @keydown.enter.prevent="toggleGroupPanel2(group, 'royalties')" @keydown.space.prevent="toggleGroupPanel2(group, 'royalties')">
                    <div><strong>Royalty Charges</strong><span class="group-panel-summary">{{ royaltiesSummary2(group) }}</span></div>
                    <CButton size="sm" color="light" @click.stop="toggleGroupPanel2(group, 'royalties')">{{ groupPanelOpen2(group, 'royalties') ? 'Collapse' : 'Expand' }}</CButton>
                  </CCardHeader>
                  <CCollapse :show="groupPanelOpen2(group, 'royalties')">
                    <CCardBody @change.native="authoritativeQuoteInputChanged2"><royalties-table :group="group" :embedded="true" /></CCardBody>
                  </CCollapse>
                </CCard>

                <CCard class="mb-3 uploads-card">
                  <CCardHeader class="group-collapsible-header" role="button" tabindex="0" :aria-expanded="groupPanelOpen2(group, 'uploads') ? 'true' : 'false'" @click="toggleGroupPanel2(group, 'uploads')" @keydown.enter.prevent="toggleGroupPanel2(group, 'uploads')" @keydown.space.prevent="toggleGroupPanel2(group, 'uploads')">
                    <div><strong>Group Uploads</strong><span class="group-panel-summary">{{ uploadsSummary2(group) }}</span></div>
                    <CButton size="sm" color="light" @click.stop="toggleGroupPanel2(group, 'uploads')">{{ groupPanelOpen2(group, 'uploads') ? 'Collapse' : 'Expand' }}</CButton>
                  </CCardHeader>
                  <CCollapse :show="groupPanelOpen2(group, 'uploads')">
                  <CCardBody>
                    <vue-dropzone
                      :ref="'groupUploader_' + group.id"
                      @vdropzone-file-added="addUploadFile2(group, group.id, ...arguments)"
                      name="files"
                      id="orderGroupUploader2"
                      :options="dropzoneOptions"
                    ></vue-dropzone>
                    <div v-if="group.uploads && group.uploads.length" class="upload-list">
                      <div v-for="(upload, uploadIndex) in group.uploads" :key="group.id + '_upload_' + uploadIndex" class="upload-row">
                        <div class="upload-meta">
                          <strong>{{ upload.name }}</strong>
                          <span v-if="upload.size"> • {{ Math.round(upload.size / 1024) }} KB</span>
                        </div>
                        <CButton size="sm" color="outline-danger" @click="removeUpload2(group, uploadIndex)">Remove</CButton>
                      </div>
                    </div>
                  </CCardBody>
                  </CCollapse>
                </CCard>

                <div class="pdf-preview-block">
                  <CButton size="sm" color="info" @click="togglePdfPreview2(group)">
                    {{ pdfPreviewOpen2(group) ? 'Hide Order PDF Preview' : 'Preview Order PDF' }}
                  </CButton>
                  <div v-if="pdfPreviewOpen2(group)" class="pdf-preview-wrap">
                    <pdf-view
                      :form="previewFormForGroup2(group)"
                      :quote-projection="quoteGroupFor2(group)"
                      :authoritative-v2="true"
                      :preview="true"
                      :type="'group2_' + group.id"
                      :current="!quoteUpdating && !quoteStale && !quoteError && !!quoteResult"
                      :diagnostics="placementDiagnosticsEnabled2()"
                    />
                  </div>
                </div>
              </CCardBody>
            </CCard>
        </div>

        <CCard class="mb-3" v-if="quoteResult">
          <CCardHeader>Promotion Discovery &amp; Guidance</CCardHeader>
          <CCardBody>
            <div class="helper-text mb-3">These results do not automatically select, apply, earn, reserve, or redeem a promotion.</div>
            <div v-if="quotePromotionRows2().length" class="promotion-guidance-list">
              <div v-for="promotion in quotePromotionRows2()" :key="promotion.bucket + '_' + promotion.promotion_id" class="promotion-guidance-row">
                <strong>{{ promotion.name || ('Promotion ' + promotion.promotion_id) }}</strong>
                <div>{{ promotionPresentation2(promotion).message }}</div>
                <div v-if="promotionPresentation2(promotion).show_price_adjustment" class="promotion-benefit">Included adjustment: -{{ formatCurrency2(promotionPresentation2(promotion).price_adjustment) }}</div>
                <div v-if="quoteDiagnostics && promotionPresentation2(promotion).diagnostic" class="quote-diagnostic">{{ promotionPresentation2(promotion).diagnostic }}</div>
              </div>
            </div>
            <div v-else class="helper-text">No promotion guidance is available for the current order.</div>
            <label v-if="placementDiagnosticsEnabled2()" class="toggle-row mt-3">
              <input type="checkbox" v-model="quoteDiagnostics" />
              <span>Show pilot diagnostics</span>
            </label>
            <pre v-if="quoteDiagnostics && placementDiagnosticsEnabled2()" class="quote-contract-diagnostics">{{ JSON.stringify(quoteDiagnosticsPayload2(), null, 2) }}</pre>
            <div v-if="quoteDiagnostics && placementDiagnosticsEnabled2()" class="quote-contract-diagnostics">
              <strong>V2 request activity (safe metadata only)</strong>
              <div v-for="(entry, requestIndex) in requestDiagnostics2()" :key="'request_diagnostic_' + requestIndex">
                {{ entry.operation }} | {{ entry.key }} | {{ entry.trigger }} | {{ entry.lifecycle }} | {{ entry.outcome }} | actual: {{ entry.actual_request_count }}
              </div>
            </div>
          </CCardBody>
        </CCard>

        <!-- <CCard>
          <CCardHeader>Submission Preview</CCardHeader>
          <CCardBody>
            <CTextarea
              rows="20"
              :value="serializedSubmitPreview2"
              readonly
            />
          </CCardBody>
        </CCard> -->

        <div class="bottom-submit-bar group-final-action-bar">
          <div class="group-final-back"><CButton color="light" @click="goBackToBuilder2">Back to Builder</CButton></div>
          <div class="bottom-submit-actions">
            <CButton v-if="blockingOrderRequirements2().length" class="group-issue-action" color="warning" @click="fixNextOrderIssue2">Fix Next Issue ({{ blockingOrderRequirements2().length }})</CButton>
            <CButton v-else-if="nextOrderRequirement2()" class="group-issue-action" color="warning" @click="fixNextOrderIssue2">{{ nextOrderRequirement2().actionLabel }}</CButton>
            <CButton color="light" @click="previousGroup2" :disabled="!hasPreviousGroup2()">Previous Group</CButton>
            <CButton color="info" @click="nextGroup2" :disabled="!hasNextGroup2()">Next Group</CButton>
            <CButton ref="reviewSubmitButton2" color="success" @click="attemptSubmitOrder2" :disabled="submitInProgress">
              Review &amp; Submit
            </CButton>
          </div>
        </div>
      </CCol>
    </CRow>
    </template>
  </div>
</template>

<script>
import { isBlankSection, v2MerchandiseGroupKey, restoreSectionIntent } from '@/blankSections2'
import EventScheduling from '@/eventScheduling2'
import EventSchedule2 from './event_schedule2.vue'
import PageBootstrap2 from './page_bootstrap2.vue'
import { initializeTiming, nativeTiming, shipDateInputKey } from '@/orderTiming2'
/* global axios, Swal */
import PdfView from '../components/pdf_view'
import AuthoritativePdfReview from './authoritative_pdf_review.vue'
import { applyAuthoritativeQuoteToPreview2 } from '../../quotePreviewProjection2'
import AdditionalChargesTable from './additional_charges_table'
import RoyaltiesTable from './royalties_table'
import PromotionPopup from './promotion_popup.vue'
import { getEligiblePromotionsForGroup, getPromotionPresentation } from './../../promotionUtils'
import { configureSections, generateLocationKey } from './../../sectionUtils'
import { buildPlacementDiagnostics, validProductClassSpecificDifference } from './../../orderGroups2PlacementDiagnostics'
import { builderMinimumPayload, MINIMUM_VERSION } from '@/builderMinimums'
import { bundlePlacementsForLine } from './../../createOrder2Wizard'
import {
  adjacentGroupId,
  dedupePricingIssues,
  displayQuotedTotal,
  groupIsReady,
  initialActiveGroupId,
  limitedChips,
  pluralizeCount,
  reconcileActiveGroup,
  selectedItemsAmount,
  selectedRoyaltyRates
} from './../../orderGroups2Navigator'
import {
  BUILDER_ISSUE_RETURN_KEY,
  blockingRequirements,
  buildOrderGroupRequirements,
  collectBuilderIssues,
  normalizeIssue,
  scrollAndFocusIssue
} from './../../issueNavigation'
import {
  buildOrderQuoteV2Payload,
  promotionResultsForDisplay,
  quoteErrorMessage,
  quoteResponseError,
  shouldApplyOrderQuoteV2Response,
  validOrderQuoteV2Response,
  withApiToken
} from './../../orderQuoteV2'
import { compactRequestFingerprint, loadCachedLookup, lookupRequestDiagnostics, stableLookupKey } from './../../orderEntryLookupCache'
import { restoreGroupReviewHandoff2 } from './../../createOrder2GroupReviewHandoff'
import { activateSavedDraft2, savedDraftIdentity2, savedDraftSubmissionFields2 } from './../../savedDraftIdentity2'
import { storageInventory, stripEphemeralPreviewUrls, writeVersionedDraft } from './../../v2DraftStorage'

var methods = {}

function clone2(value) {
  return JSON.parse(JSON.stringify(value))
}

var CREATE_ORDER2_BUILDER_DRAFT_KEY = 'create_order2_builder_draft_v1'
var ORDER_GROUPS2_DRAFT_KEY = 'create_order2_group_review_draft_v1'

function uid2(prefix) {
  return prefix + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8)
}

function billedFromSectionLoc2(sLoc) {
  const qty = Number(sLoc?.quantity || 0)
  const free = Number(sLoc?.promotion_free_pieces || 0)
  return Math.max(0, qty - free)
}

function readUnitFrom2(sLoc, itemLoc) {
  const a = Number(sLoc?.cost)
  if (!Number.isNaN(a)) return a

  const b = Number(itemLoc?.cost)
  if (!Number.isNaN(b)) return b

  return 0
}

function keyForSectionLoc2(sloc) {
  const techniqueId = parseInt(sloc?.technique?.technique_id || sloc?.technique_id || 0, 10) || 0
  const locationId = parseInt(sloc?.location?.location_id || sloc?.location_id || 0, 10) || 0
  const assetName = String(
    sloc?.decoration?.name ||
    sloc?.flow_image?.name ||
    sloc?.new_decoration_data?.name ||
    sloc?.file_name ||
    sloc?.decoration_text ||
    ''
  ).trim().toLowerCase()
  const layout = String(sloc?.layout_text || '').trim().toLowerCase()
  return [techniqueId, locationId, assetName, layout].join('|')
}

function stampBilledFromSectionsIntoForm2(form, sectionObj) {
  if (!form || !sectionObj) return

  const index = new Map()
  ;(form.order_items || []).forEach((item) => {
    (item.order_item_locations || []).forEach((loc) => {
      const k = generateLocationKey(loc)
      if (!index.has(k)) index.set(k, [])
      index.get(k).push(loc)
    })
  })

  Object.values(sectionObj).forEach((section) => {
    const locs = section?.locations_object || {}
    Object.entries(locs).forEach(([k, sLoc]) => {
      const itemLocs = index.get(k) || index.get(keyForSectionLoc2(sLoc)) || []
      if (!itemLocs.length) return

      const billed = billedFromSectionLoc2(sLoc)
      const unit = readUnitFrom2(sLoc, itemLocs[0])
      const ext = +(unit * billed).toFixed(2)

      for (const itemLoc of itemLocs) {
        itemLoc.billed_quantity = billed
        itemLoc.cost = unit
        itemLoc.technique_cost = ext
        itemLoc.promotion_applied = !!sLoc?.promotion_applied
        itemLoc.promotion_free_pieces = Number(sLoc?.promotion_free_pieces || 0)
      }
    })
  })
}


methods.customerSummary2 = function () {
  var customer = this.draft && this.draft.builder ? this.draft.builder.customer_info : null
  if (!customer) return ''
  return [customer.customer_name, customer.customer_number].filter(Boolean).join(' - ')
}

methods.shipToSummary2 = function () {
  var builder = this.draft && this.draft.builder ? this.draft.builder : null
  if (!builder) return ''
  if (builder.new_ship_to && builder.new_ship_info) {
    return [builder.new_ship_info.ship_name, builder.new_ship_info.address_1, builder.new_ship_info.city].filter(Boolean).join(' - ')
  }
  var shipTo = builder.ship_to_info || null
  if (!shipTo) return ''
  return [shipTo.ship_to_number, shipTo.ship_name, shipTo.address_1].filter(Boolean).join(' - ')
}

methods.eventGroupLabel2 = function () {
  var group = ((this.draft && this.draft.groups) || []).find(g => g.id === (this.eventResult2 && this.eventResult2.controlling_group_id))
  return group ? [group.program_info && group.program_info.program_name, group.name].filter(Boolean).join(' / ') || group.id : ''
}
methods.schedulingForm2 = function () { return this.draft && this.draft.builder }
methods.schedulingGroups2 = function () { return this.draft ? this.shipDatePayload2().groups : [] }
methods.applyEventTiming2 = function (intent) {
  this.$set(this.draft.builder, 'scheduling_intent', intent)
  this.$set(this.draft.builder, 'scheduling_intent_updated_at', new Date().toISOString())
  this.syncBuilderTiming2(); this.persistDraft2()
}
methods.changeEventConfiguration2 = function () {
  this.eventModal2 = false
  var group = (this.draft.groups || []).find(g => g.id === (this.eventResult2 && this.eventResult2.controlling_group_id))
  var item = group && group.items && group.items[0]
  this.routeIssueToBuilder2({ step: 3, entityType: 'section', entityId: item && item.section_id, sectionId: item && item.section_id, target: 'section_card_' + (item && item.section_id) })
}

methods.prebookEligible2 = function () {
  var futureShipDate = this.draft && this.draft.order ? this.draft.order.future_ship_date : ''
  if (!futureShipDate) {
    return false
  }

  var selectedDate = new Date(futureShipDate)
  if (isNaN(selectedDate.getTime())) {
    return false
  }

  var today = new Date()
  var ninetyDaysFromToday = new Date()
  ninetyDaysFromToday.setDate(today.getDate() + 90)

  return selectedDate >= ninetyDaysFromToday
}

methods.normalizePrebookOption2 = function (value) {
  if (!value) return ''
  // An unavailable catalog is not evidence that a saved choice was removed.
  if (!this.prebookCatalogLoaded2) return ''
  var id = value && typeof value === 'object' ? value.id : null
  var label = typeof value === 'string' ? value : value.pre_book_label
  var matches = (this.preBookOptions || []).filter(function (option) {
    return id != null ? String(option.id) === String(id) : option.pre_book_label === label
  })
  if (matches.length === 1) return clone2(matches[0])
  this.prebookNotice2 = 'The saved Prebook option is no longer available. Select an available option if applicable.'
  return ''
}

methods.loadPrebookOptions2 = function () {
  var self = this
  var key = stableLookupKey('prebook-options', {})
  return loadCachedLookup(key, function () {
    return axios.post(self.$apiAdress + '/api/getPrebookOptions?token=' + localStorage.getItem('api_token')).then(function (response) {
      return Array.isArray(response.data) ? response.data : []
    })
  }, { operation: 'prebook_options', endpoint: 'POST /api/getPrebookOptions', trigger: 'review_init', lifecycle: 'hydrating' })
    .then((options) => {
      this.prebookCatalogLoaded2 = true
      this.prebookCatalogError2 = false
      this.preBookOptions = clone2(options)
      if (this.draft && this.draft.order) {
        if (this.prebookCatalogLoaded2) this.draft.order.prebook = this.normalizePrebookOption2(this.draft.order.prebook)
      }
    })
    .catch((error) => {
      this.$store.commit('axios_error', error)
      this.prebookCatalogLoaded2 = false
      this.prebookCatalogError2 = true
      this.preBookOptions = []
    })
}

methods.formatCurrency2 = function (value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(parseFloat(value || 0) || 0)
}

methods.groupReady2 = function (group) {
  return groupIsReady(group) && this.groupIssues2(group).length === 0
}

methods.groupStyleChips2 = function (group) {
  return limitedChips(group.items || [], function (item) {
    return (item.style_name || 'Style') + ' · ' + (parseInt(item.units || 0, 10) || 0)
  })
}

methods.groupDecorations2 = function (group) {
  var itemRefs = {}
  ;(group.items || []).forEach(function (item) {
    itemRefs[(item.section_id || '') + ':' + (item.line_id || '')] = true
  })
  var decorations = []
  ;((this.draft && this.draft.legacy && this.draft.legacy.order_items) || []).forEach(function (item) {
    if (!itemRefs[(item.section_id || '') + ':' + (item.line_id || '')]) return
    decorations = decorations.concat(item.order_item_locations || [])
  })
  return decorations
}

methods.groupDecorationChips2 = function (group) {
  return limitedChips(this.groupDecorations2(group), function (decoration) {
    var technique = decoration.technique && decoration.technique.technique_name
      ? decoration.technique.technique_name
      : 'Technique needed'
    var location = decoration.location
      ? (decoration.location.location_code || decoration.location.location_name || 'Location needed')
      : 'Location needed'
    return technique + ' @ ' + location
  })
}

methods.countLabel2 = function (count, singular, plural) {
  return pluralizeCount(count, singular, plural)
}

methods.groupDisplayName2 = function (group, groupIndex) {
  var name = group && group.name ? String(group.name).trim() : ''
  var genericNames = ['group ' + (groupIndex + 1), 'order group ' + (groupIndex + 1)]
  return genericNames.indexOf(name.toLowerCase()) > -1 ? '' : name
}

methods.builderIssuesForReview2 = function () {
  var builder = this.draft && this.draft.builder ? this.draft.builder : {}
  return collectBuilderIssues(builder, {
    // Handoff retains selected IDs, not option catalogs; submission rehydrates them.
    merchandiseCatalogRequired: false,
    schedulingRequirements: this.schedulingRequirements2 ? this.schedulingRequirements2() : [],
    repRequired: builder.rep === '' || builder.rep === null,
    designFirst: true,
    shipReady: function (form) {
      return !!(form.ship_to_info || (form.new_ship_to && form.new_ship_info && form.new_ship_info.address_1))
    },
    hasArtwork: function (placement) {
      return !!(placement && (placement.artwork || placement.artwork_name || placement.artwork_preview_url || placement.decoration_text))
    },
    lineReady: function (line) {
      return !!(line && line.style_info && line.style_info.style_id && (parseInt(line.units || 0, 10) || 0) > 0)
    }
  })
}

methods.blankRecoveryValue2 = function (value) {
  if (typeof value === 'string') { try { const decoded = JSON.parse(value); if (decoded && typeof decoded === 'object') return this.blankRecoveryValue2(decoded) } catch (error) { /* Historical text is already displayable. */ } }
  if (Array.isArray(value)) return value.map(this.blankRecoveryValue2).join('; ')
  if (value && typeof value === 'object') return [value.charge_name || value.name || value.pre_book_label || 'Saved selection', value.price != null ? '$' + Number(value.price).toFixed(2) : '', value.id || value.additional_charges_id ? '(reference ' + (value.id || value.additional_charges_id) + ')' : ''].filter(Boolean).join(' ')
  return String(value)
}
methods.resolveBlankRecovery2 = async function (row) {
  const result = await Swal.fire({ title: 'Confirm Imported Value Review', text: 'Confirm that you have assigned this value to the appropriate Order Group using its controls, or deliberately excluded it. The original value remains in this draft’s recovery history.', showCancelButton: true, confirmButtonText: 'I Have Reviewed This Value' })
  if (result.isConfirmed) { this.$set(row, 'status', 'resolved'); this.persistDraft2() }
}
methods.orderIssues2 = function () {
  var issues = this.builderIssuesForReview2().map(function (issue) {
    return Object.assign({}, issue, { action: 'builder', builderAction: issue.action })
  })
  ;((this.draft && this.draft.builder && this.draft.builder.blank_recovery) || []).filter(row => row.status !== 'resolved').forEach(row => issues.push(normalizeIssue({ code: 'legacy_group_value_recovery', message: 'Review imported ' + row.field + ' from legacy group ' + row.source_group_id + '.', blocking: true, target: 'blank_recovery2' })))
  ;((this.draft && this.draft.groups) || []).forEach(function (group) {
    if (!group.ship_date) {
      issues.push(normalizeIssue({ code: 'ship_date_pending_configuration', state: 'pending', severity: 'info', contributesToIssueCount: false, message: 'Ship Date is pending complete Program, quantity, and Decoration configuration.', step: 0, entityType: 'group', entityId: group.id, target: 'group_ship_date_' + group.id }))
    }
  })
  issues = issues.concat(buildOrderGroupRequirements((this.draft && this.draft.groups) || [], {
    shipViaOptions: this.shipViaOptions,
    shipViaOptionsReady: this.shipViaOptionsReady2,
    reviewAttempted: this.reviewAttempted2
  }))
  if (this.quoteHasBlockingIssues2()) {
    issues.push(normalizeIssue({ code: 'pricing_needs_review', message: 'Pricing configuration needs review.', step: 0, entityType: 'pricing', target: 'pricing_needs_review', action: 'pricing' }))
  }
  return issues
}

methods.blockingOrderRequirements2 = function () {
  return blockingRequirements(this.orderIssues2())
}

methods.nextOrderRequirement2 = function () {
  return this.orderIssues2().find(function (row) { return row.state === 'blocking_issue' }) ||
    this.orderIssues2().find(function (row) { return row.state === 'next_action' }) || null
}

methods.attemptSubmitOrder2 = function () {
  if (!this.canSubmit2()) {
    this.reviewAttempted2 = true
    var next = this.nextOrderRequirement2()
    if (next) this.fixNextOrderIssue2()
    window.Swal.fire({ icon: 'warning', title: 'Order needs attention', text: next && next.message ? next.message : 'Complete the highlighted order requirement before reviewing PDFs.', confirmButtonText: 'Close' })
    return false
  }
  return this.openPdfReview2()
}

methods.reviewFingerprint2 = function () {
  if (!this.draft) return ''
  var stableDraft = stripEphemeralPreviewUrls(this.draft)
  if (Array.isArray(stableDraft.groups)) {
    stableDraft.groups = stableDraft.groups.slice().sort(function (a, b) { return String(a.id).localeCompare(String(b.id)) })
  }
  return JSON.stringify({
    draft: stableDraft,
    quote: this.quoteSuccessfulFingerprint2,
    minimum: this.minimumResultFingerprint2 || '',
    event: this.eventResultKey2 || '',
    ship_dates: (this.draft.groups || []).map(function (group) { return [group.id, group.ship_date, group.ship_via_code || (group.ship_via && group.ship_via.ship_via_code) || ''] })
  }, function (key, value) {
    if (['evaluation', 'expanded', 'loading', 'error', 'showPromoPopup'].indexOf(key) !== -1) return undefined
    return value
  })
}
methods.openPdfReview2 = function () {
  var groups = (this.draft && this.draft.groups) || []
  if (!groups.length) return false
  var fingerprint = this.reviewFingerprint2()
  var groupIds = groups.map(function (group) { return String(group.id) })
  var unchanged = this.pdfReview2.fingerprint === fingerprint && !this.pdfReview2.stale
  var viewed = {}
  if (unchanged) {
    groupIds.forEach(function (id) { if (this.pdfReview2.viewed[id]) viewed[id] = true }, this)
  }
  var previousId = this.pdfReview2.groupId
  var index = unchanged && previousId ? groupIds.indexOf(String(previousId)) : -1
  if (index < 0 || (Object.keys(viewed).length < groupIds.length && viewed[groupIds[index]])) {
    index = groupIds.findIndex(function (id) { return !viewed[id] })
  }
  if (index < 0) index = 0
  this.pdfReview2 = { open: true, index: index, groupId: groupIds[index], fingerprint: fingerprint, viewed: viewed, stale: false }
  return true
}
methods.closePdfReview2 = function () {
  if (!this.submitInProgress) {
    var current = this.reviewCurrentGroup2()
    this.pdfReview2.groupId = current ? String(current.id) : this.pdfReview2.groupId
    this.pdfReview2.open = false
  }
}
methods.restoreReviewSubmitFocus2 = function () {
  this.$nextTick(() => {
    var button = this.$refs.reviewSubmitButton2
    if (button && button.$el) button = button.$el
    if (button && button.focus) button.focus()
  })
}
methods.reviewCurrentGroup2 = function () {
  return this.draft && this.draft.groups ? this.draft.groups[this.pdfReview2.index] || null : null
}
methods.reviewGroupLabel2 = function (group) {
  return group ? [group.program_info && group.program_info.program_name, group.name].filter(Boolean).join(' / ') || ('Group ' + (this.pdfReview2.index + 1)) : ''
}
methods.showReviewGroup2 = function (index) {
  var groups = (this.draft && this.draft.groups) || []
  if (index < 0 || index >= groups.length || this.submitInProgress) return false
  this.pdfReview2.index = index
  this.pdfReview2.groupId = String(groups[index].id)
  this.$nextTick(() => {
    var body = this.$refs.pdfReviewBody2
    if (body) body.scrollTop = 0
  })
  return true
}
methods.markReviewGroupRendered2 = function (group) {
  if (!this.pdfReview2.open || !group || this.pdfReview2.stale) return false
  this.$set(this.pdfReview2.viewed, String(group.id), true)
  return true
}
methods.groupReviewed2 = function (group) {
  return !!(group && this.pdfReview2.viewed[String(group.id)])
}
methods.reviewedGroupCount2 = function () {
  return ((this.draft && this.draft.groups) || []).filter(this.groupReviewed2).length
}
methods.allReviewGroupsViewed2 = function () {
  var groups = (this.draft && this.draft.groups) || []
  return !!groups.length && groups.every(this.groupReviewed2) && !this.pdfReview2.stale && this.pdfReview2.fingerprint === this.reviewFingerprint2()
}
methods.invalidatePdfReview2 = function () {
  if (!this.pdfReview2.open) return
  if (this.pdfReview2.fingerprint !== this.reviewFingerprint2()) {
    this.pdfReview2.viewed = {}
    this.pdfReview2.stale = true
  }
}
methods.finalSubmitReviewedOrder2 = function () {
  if (this.submitInProgress || !this.allReviewGroupsViewed2()) return false
  if (!this.canSubmit2()) {
    this.pdfReview2.open = false
    return this.attemptSubmitOrder2()
  }
  return this.submitOrder2()
}

methods.groupIssues2 = function (group) {
  if (!group) return []
  var sectionIds = group.source_section_ids || []
  var bundleIds = (group.items || []).map(function (item) { return item.bundle_id }).filter(Boolean)
  var lineIds = (group.items || []).map(function (item) { return item.line_id }).filter(Boolean)
  return this.orderIssues2().filter(function (issue) {
    if (issue.entityType === 'pricing') return true
    if (issue.entityType === 'group') return issue.entityId === group.id
    if (issue.step === 1) return false
    if (issue.entityType === 'design') return bundleIds.indexOf(issue.entityId) > -1
    if (issue.entityType === 'section') return sectionIds.indexOf(issue.entityId) > -1
    if (issue.entityType === 'style') return lineIds.indexOf(issue.entityId) > -1
    return false
  })
}

methods.firstGroupIssue2 = function (group) {
  return this.groupIssues2(group)[0] || null
}

methods.routeIssueToBuilder2 = function (issue) {
  sessionStorage.setItem(BUILDER_ISSUE_RETURN_KEY, JSON.stringify(Object.assign({}, issue, {
    action: issue.builderAction || ''
  })))
  this.goBackToBuilder2()
}

methods.fixFirstGroupIssue2 = function (group, groupIndex) {
  this.selectGroup2(group, groupIndex)
  var issue = this.firstGroupIssue2(group)
  if (!issue) {
    issue = normalizeIssue({ target: 'group_attention_' + group.id, entityType: 'group', entityId: group.id })
  }
  this.navigateOrderIssue2(issue, group)
}

methods.navigateOrderIssue2 = function (issue, group) {
  if (issue.code === 'event_schedule') { this.refreshEventSchedule2(); return }
  if (issue.action === 'builder') return this.routeIssueToBuilder2(issue)
  if (issue.panel && group) this.$set(this.groupPanelState2, this.groupPanelKey2(group, issue.panel), true)
  this.$nextTick(function () {
    scrollAndFocusIssue(issue, {
      document: document,
      window: window,
      fallbackTarget: group ? 'group_attention_' + group.id : issue.target,
      setTimeout: setTimeout
    })
  })
}

methods.fixNextOrderIssue2 = function () {
  var issue = this.nextOrderRequirement2()
  if (!issue) return
  var groups = this.draft && this.draft.groups ? this.draft.groups : []
  var groupIndex = issue.entityType === 'group'
    ? groups.findIndex(function (group) { return group.id === issue.entityId })
    : this.activeGroupIndex2()
  if (groupIndex < 0) groupIndex = 0
  var group = groups[groupIndex] || null
  if (group) this.selectGroup2(group, groupIndex)
  this.navigateOrderIssue2(issue, group)
}

methods.activeGroup2 = function () {
  var groups = this.draft && this.draft.groups ? this.draft.groups : []
  return groups.find(function (group) { return group.id === this.activeGroupId2 }, this) || null
}

methods.initializeActiveGroup2 = function () {
  var groups = this.draft && this.draft.groups ? this.draft.groups : []
  var incomplete = groups.find(function (group) { return !this.groupReady2(group) }, this)
  this.activeGroupId2 = incomplete ? incomplete.id : initialActiveGroupId(groups)
  this.activeGroupPosition2 = Math.max(0, groups.findIndex(function (group) {
    return group.id === this.activeGroupId2
  }, this))
}

methods.ensureActiveGroup2 = function () {
  var groups = this.draft && this.draft.groups ? this.draft.groups : []
  var next = reconcileActiveGroup(groups, this.activeGroupId2, this.activeGroupPosition2)
  this.activeGroupId2 = next.id
  this.activeGroupPosition2 = next.index
}

methods.selectGroup2 = function (group, groupIndex) {
  if (!group) return
  this.activeGroupId2 = group.id
  this.activeGroupPosition2 = groupIndex
}

methods.activeGroupIndex2 = function () {
  var groups = this.draft && this.draft.groups ? this.draft.groups : []
  return groups.findIndex(function (group) {
    return group.id === this.activeGroupId2
  }, this)
}

methods.hasPreviousGroup2 = function () {
  return this.activeGroupIndex2() > 0
}

methods.hasNextGroup2 = function () {
  var groups = this.draft && this.draft.groups ? this.draft.groups : []
  var index = this.activeGroupIndex2()
  return index >= 0 && index < groups.length - 1
}

methods.previousGroup2 = function () {
  var groups = this.draft && this.draft.groups ? this.draft.groups : []
  var id = adjacentGroupId(groups, this.activeGroupId2, -1)
  var index = groups.findIndex(function (group) { return group.id === id })
  if (index >= 0) this.selectGroup2(groups[index], index)
}

methods.nextGroup2 = function () {
  var groups = this.draft && this.draft.groups ? this.draft.groups : []
  var id = adjacentGroupId(groups, this.activeGroupId2, 1)
  var index = groups.findIndex(function (group) { return group.id === id })
  if (index >= 0) this.selectGroup2(groups[index], index)
}

methods.groupPanelKey2 = function (group, panel) {
  return (group ? group.id : '') + ':' + panel
}

methods.additionalChargesSummary2 = function (group) {
  var selected = group && group.additional_charges ? group.additional_charges : []
  if (!selected.length) return 'None added'
  var quoteGroup = this.quoteGroupFor2(group)
  var quoteAmount = quoteGroup && quoteGroup.totals ? quoteGroup.totals.additional_charges : null
  var amount = quoteAmount === null || quoteAmount === undefined ? selectedItemsAmount(selected) : quoteAmount
  return this.countLabel2(selected.length, 'selected item') + ' · ' + this.formatCurrency2(amount)
}

methods.royaltiesSummary2 = function (group) {
  var selected = group && group.royalty_charges ? group.royalty_charges : []
  if (!selected.length) return 'None added'
  var rates = selectedRoyaltyRates(selected)
  var quoteGroup = this.quoteGroupFor2(group)
  var quoteAmount = quoteGroup && quoteGroup.totals ? quoteGroup.totals.royalties : null
  var amount = quoteAmount === null || quoteAmount === undefined ? selectedItemsAmount(selected) : quoteAmount
  var parts = [this.countLabel2(selected.length, 'selected item')]
  if (rates.length) parts.push('Rate ' + rates.join(', '))
  parts.push(this.formatCurrency2(amount))
  return parts.join(' · ')
}

methods.uploadsSummary2 = function (group) {
  var uploads = group && group.uploads ? group.uploads : []
  return uploads.length ? this.countLabel2(uploads.length, 'file') : 'None added'
}

methods.groupPanelOpen2 = function (group, panel) {
  return !!this.groupPanelState2[this.groupPanelKey2(group, panel)]
}

methods.toggleGroupPanel2 = function (group, panel) {
  var key = this.groupPanelKey2(group, panel)
  this.$set(this.groupPanelState2, key, !this.groupPanelState2[key])
}

methods.pdfPreviewOpen2 = function (group) {
  return !!this.pdfPreviewState2[group ? group.id : '']
}

methods.togglePdfPreview2 = function (group) {
  if (!group) return
  this.$set(this.pdfPreviewState2, group.id, !this.pdfPreviewState2[group.id])
}

methods.groupPricingHeading2 = function () {
  return this.quoteHasBlockingIssues2() ? 'Provisional Pricing Breakdown' : 'Authoritative Pricing Breakdown'
}

methods.pricingComponentLabel2 = function (label) {
  return this.quoteHasBlockingIssues2() ? 'Provisional ' + label.toLowerCase() : label
}

methods.displayCurrencyOrUnavailable2 = function (value) {
  if (value === null || value === undefined || value === '') return 'Unavailable'
  return this.formatCurrency2(value)
}

methods.groupTotalDisplay2 = function (group) {
  var quoteGroup = this.quoteGroupFor2(group)
  var value = quoteGroup && quoteGroup.totals ? quoteGroup.totals.quoted_group_total : null
  return displayQuotedTotal(value, this.quoteHasBlockingIssues2(), this.formatCurrency2)
}

methods.orderTotalDisplay2 = function () {
  var value = this.quoteResult && this.quoteResult.totals ? this.quoteResult.totals.quoted_order_total : null
  return displayQuotedTotal(value, this.quoteHasBlockingIssues2(), this.formatCurrency2)
}

methods.buildQuotePayload2 = function () {
  return buildOrderQuoteV2Payload(this.buildSubmitPreview2())
}

methods.quoteGroupFor2 = function (group) {
  if (!this.quoteResult || !group) return null
  return (this.quoteResult.groups || []).find(quoteGroup => String(quoteGroup.group_id) === String(group.id)) || null
}

methods.quoteHasBlockingIssues2 = function () {
  return !!(this.quoteResult && (
    this.quoteResult.authoritative === false ||
    this.quoteResult.quote_status === 'pricing_needs_review' ||
    (this.quoteResult.blocking_errors || []).length
  ))
}

methods.quoteBlockingReasons2 = function () {
  if (!this.quoteResult) return []
  return dedupePricingIssues(this.quoteResult.blocking_errors || []).map(function (issue) {
    return {
      message: issue && issue.message ? issue.message : 'Required pricing configuration could not be resolved.'
    }
  })
}

methods.quoteDiagnosticsPayload2 = function () {
  if (!this.quoteResult) return {}
  var decorationLines = []
  ;(this.quoteResult.groups || []).forEach(function (group) {
    (group.items || []).forEach(function (item) {
      decorationLines = decorationLines.concat(item.locations || [])
    })
  })
  return {
    warnings: this.quoteResult.warnings || [],
    blocking_errors: this.quoteResult.blocking_errors || [],
    decoration_lines: decorationLines
  }
}

methods.showGroupPromotionAdjustment2 = function (group) {
  var quoteGroup = this.quoteGroupFor2(group)
  return !!(quoteGroup && Number(quoteGroup.totals.promotion_adjustment) > 0)
}

methods.quotePromotionRows2 = function () {
  return this.quoteResult ? promotionResultsForDisplay(this.quoteResult.promotions) : []
}

methods.promotionPresentation2 = function (promotion) {
  return getPromotionPresentation(promotion, { pilotDiagnostics: this.quoteDiagnostics })
}

methods.requestDiagnostics2 = function () {
  return lookupRequestDiagnostics().slice(-40).reverse()
}

methods.cancelQuoteRequest2 = function () {
  if (this.quoteCancelSource2) {
    this.quoteCancelSource2.cancel('Superseded V2 quote request')
    this.quoteCancelSource2 = null
  }
}

methods.queueQuoteRefresh2 = function (options) {
  options = options || {}
  if (this.quoteHydrating) return false

  var readiness = this.quoteReadiness2()
  if (!readiness.ready) {
    if (readiness.reason === 'quote_payload_unavailable') {
      this.quoteError = 'Unable to prepare the authoritative quote.'
    }
    return false
  }
  var payload = readiness.payload
  var fingerprint = readiness.fingerprint
  if (!options.force && fingerprint === this.quotePendingFingerprint2) return false

  this.quotePendingFingerprint2 = fingerprint
  this.quoteRequestSequence2 += 1
  var sequence = this.quoteRequestSequence2
  this.cancelQuoteRequest2()
  if (this.quoteRefreshTimer2) clearTimeout(this.quoteRefreshTimer2)

  if (!options.force && this.quoteResult && fingerprint === this.quoteSuccessfulFingerprint2) {
    this.quoteUpdating = false
    this.quoteStale = false
    this.quoteError = ''
    this.quoteRefreshTimer2 = null
    return false
  }

  this.quoteUpdating = true
  this.quoteRefreshTimer2 = setTimeout(() => {
    this.quoteRefreshTimer2 = null
    this.requestQuote2(payload, fingerprint, sequence)
  }, options.immediate ? 0 : 350)
  return true
}

methods.quoteReadiness2 = function () {
  if (!this.draft) return { ready: false, reason: 'draft_not_loaded' }
  if (this.missingDraft) return { ready: false, reason: 'draft_missing' }
  if (!this.draft.builder || !this.draft.order) return { ready: false, reason: 'draft_incomplete' }
  if (!Array.isArray(this.draft.groups) || !this.draft.groups.length) return { ready: false, reason: 'groups_not_ready' }

  try {
    var payload = this.buildQuotePayload2()
    return {
      ready: true,
      reason: 'ready',
      payload: payload,
      fingerprint: JSON.stringify(payload)
    }
  } catch (error) {
    return { ready: false, reason: 'quote_payload_unavailable' }
  }
}

methods.requestQuote2 = function (payload, fingerprint, sequence) {
  var config = withApiToken()
  var key = stableLookupKey('order-quote-v2', { fingerprint: compactRequestFingerprint(fingerprint) })
  return loadCachedLookup(key, () => {
    return axios.post(this.$apiAdress + '/api/orders/v2/evaluate', payload, config).then(function (response) {
      var authorizationError = quoteResponseError(response)
      if (authorizationError) throw authorizationError
      if (!validOrderQuoteV2Response(response.data)) {
        throw new Error('The quote service returned an unsupported response contract.')
      }
      return response.data
    })
  }, { operation: 'authoritative_quote', endpoint: 'POST /api/orders/v2/evaluate', trigger: 'pricing_input_changed', lifecycle: 'review' }).then(result => {
    if (!shouldApplyOrderQuoteV2Response(sequence, this.quoteRequestSequence2)) return
    this.quoteResult = clone2(result)
    this.quoteSuccessfulFingerprint2 = fingerprint
    this.quoteUpdating = false
    this.quoteStale = false
    this.quoteError = ''
  }).catch(error => {
    if (!shouldApplyOrderQuoteV2Response(sequence, this.quoteRequestSequence2) || (axios.isCancel && axios.isCancel(error))) return
    this.quoteUpdating = false
    this.quoteStale = !!this.quoteResult
    if (sequence === this.quoteRequestSequence2) this.quotePendingFingerprint2 = ''
    this.quoteError = error && error.message && error.message.indexOf('unsupported response contract') > -1
      ? error.message
      : quoteErrorMessage(error)
  }).finally(() => {
    if (sequence === this.quoteRequestSequence2) this.quoteCancelSource2 = null
  })
}

methods.authoritativeQuoteInputChanged2 = function () {
  if (this.quoteHydrating) return
  this.queueRefreshGroupEvaluations2()
  this.queueQuoteRefresh2()
  this.queueShipDateRefresh2()
}

methods.shipDatePayload2 = function () {
  var intent = this.draft && this.draft.builder && this.draft.builder.scheduling_intent
  var timing = intent ? nativeTiming(intent) : (this.draft && this.draft.order || {})
  var acceptedFastLane = !!(intent && intent.fast_lane && this.draft.builder.fast_lane_accepted_fingerprint && this.draft.builder.fast_lane_accepted_event_date === intent.in_hands_date)
  return {
    future_ship_date_boolean: !!timing.future_ship_date_boolean,
    future_ship_date: timing.future_ship_date_boolean ? (timing.future_ship_date || null) : null,
    fast_lane: acceptedFastLane,
    groups: ((this.draft && this.draft.groups) || []).map(function (group) {
      return { group_id: group.id, order_items: clone2(this.previewFormForGroup2(group).order_items || []) }
    }, this)
  }
}

methods.refreshShipDates2 = function () {
  if (!this.draft || !this.draft.groups || !this.draft.groups.length) { this.shipDateUpdating2 = false; return Promise.resolve(false) }
  var payload = this.shipDatePayload2()
  var fingerprint = shipDateInputKey(payload, this.draft.builder && this.draft.builder.scheduling_intent)
  if (this._pendingShipDates2 && this._pendingShipDates2.key === fingerprint && this._pendingShipDates2.generation === this.shipDateRequestGeneration2) return this._pendingShipDates2.promise
  var generation = ++this.shipDateRequestGeneration2
  this.shipDateUpdating2 = true
  this.timingFastLaneConfirmed2 = false
  if (payload.fast_lane && this.eventActive2) { this.eventResultKey2 = ''; this.eventGeneration2 += 1 }
  if (this.draft.builder && this.draft.builder.scheduling_intent) this.syncBuilderTiming2()
  this.shipDateError2 = ''
  var request = axios.post(this.$apiAdress + '/api/orders/v2/calculate-ship-dates?token=' + localStorage.getItem('api_token'), payload).then((response) => {
    if (generation !== this.shipDateRequestGeneration2 || fingerprint !== shipDateInputKey(this.shipDatePayload2(), this.draft.builder && this.draft.builder.scheduling_intent)) return false
    var byId = {}
    ;((response.data && response.data.groups) || []).forEach(function (result) { byId[result.group_id] = result })
    ;(this.draft.groups || []).forEach(function (group) {
      var result = byId[group.id]
      group.ship_date = result && result.status === 'calculated' ? result.ship_date : ''
      group.ship_asap = result ? result.ship_asap : 1
      group.do_not_ship_early = result ? result.do_not_ship_early : 0
      group.ship_date_constraint = result ? result.determining_constraint : null
    })
    this.timingFastLaneConfirmed2 = !!payload.fast_lane && (this.draft.groups || []).every(group => byId[group.id] && byId[group.id].status === 'calculated')
    if (this.draft.builder && this.draft.builder.scheduling_intent) {
      this.syncBuilderTiming2()
      if (this.queueQuoteRefresh2) this.queueQuoteRefresh2()
    }
    if (this.eventActive2) this.refreshEventSchedule2(false)
    return true
  }).catch((error) => {
    if (generation !== this.shipDateRequestGeneration2 || fingerprint !== shipDateInputKey(this.shipDatePayload2(), this.draft.builder && this.draft.builder.scheduling_intent)) return false
    ;(this.draft.groups || []).forEach(function (group) {
      group.ship_date = ''
      group.ship_date_constraint = null
    })
    this.shipDateError2 = (error.response && error.response.data && error.response.data.errors && Object.values(error.response.data.errors)[0][0]) || 'Ship Date could not be calculated.'
    return false
  }).finally(() => {
    if (generation === this.shipDateRequestGeneration2) this.shipDateUpdating2 = false
    if (this._pendingShipDates2 && this._pendingShipDates2.promise === request) this._pendingShipDates2 = null
  })
  this._pendingShipDates2 = { key: fingerprint, generation, promise: request }
  return request
}

methods.queueShipDateRefresh2 = function (immediate) {
  var key = this.draft ? shipDateInputKey(this.shipDatePayload2(), this.draft.builder && this.draft.builder.scheduling_intent) : ''
  if (this._pendingShipDates2 && this._pendingShipDates2.key === key && this._pendingShipDates2.generation === this.shipDateRequestGeneration2) return this._pendingShipDates2.promise
  if (this.shipDateRefreshTimer2 && this._queuedShipDateKey2 === key) return
  this._queuedShipDateKey2 = key
  this.shipDateRequestGeneration2 += 1
  this.shipDateUpdating2 = true
  this.timingFastLaneConfirmed2 = false
  if (this.draft && this.draft.builder && this.draft.builder.scheduling_intent) this.syncBuilderTiming2()
  if (this.shipDateRefreshTimer2) clearTimeout(this.shipDateRefreshTimer2)
  this.shipDateRefreshTimer2 = setTimeout(() => {
    this.shipDateRefreshTimer2 = null
    this.refreshShipDates2()
  }, immediate ? 0 : 250)
}

methods.retryQuote2 = function () {
  this.queueQuoteRefresh2({ immediate: true, force: true })
}

methods.editOrderTiming2 = function () {
  sessionStorage.setItem(BUILDER_ISSUE_RETURN_KEY, JSON.stringify({ action: 'edit_order_timing', step: 1, target: 'order_timing2' }))
  this.goBackToBuilder2()
}
methods.syncBuilderTiming2 = function () {
  if (!this.draft || !this.draft.builder.scheduling_intent) return
  Object.assign(this.draft.order, nativeTiming(this.draft.builder.scheduling_intent, this.timingFastLaneConfirmed2))
  this.syncFastLaneAcrossGroups2()
}

methods.goBackToBuilder2 = function () {
  this.persistDraft2()
  sessionStorage.setItem('create_order2_collapse_on_return_v1', '1')
  var activeSaved = savedDraftIdentity2(this.draft && this.draft.builder)
  this.$router.push({ path: activeSaved ? '/create_order2/' + activeSaved.saved_id + '/saved' : '/create_order2' })
}

methods.draftHasMeaningfulData2 = function () {
  var builder = this.draft && this.draft.builder ? this.draft.builder : {}

  return !!(
    builder.po_number ||
    builder.customer_info ||
    builder.ship_to_info ||
    builder.new_ship_to ||
    builder.rep ||
    (builder.bundles && builder.bundles.length) ||
    (builder.sections && builder.sections.length) ||
    (this.draft && this.draft.groups && this.draft.groups.length)
  )
}

methods.resetGroupReviewState2 = function () {
  this.clearDraft2()
  sessionStorage.removeItem(CREATE_ORDER2_BUILDER_DRAFT_KEY)
  sessionStorage.removeItem('create_order2_group_review')
  this.$router.push({ path: '/create_order2' })
}

methods.saveBuilderForLaterFromReview2 = function () {
  if (!this.draft || !this.draft.builder) {
    return Promise.resolve(false)
  }

  var builder = clone2(this.draft.builder)
  var activeSavedFields = savedDraftSubmissionFields2(builder)
  builder.prebook = clone2(this.draft.order.prebook || '')
  builder.imported_review_order = {
    prebook: clone2(this.draft.order.prebook || ''),
    available_emails: clone2(this.draft.order.available_emails || []),
    extra_emails: this.draft.order.extra_emails || '',
    email_text: this.draft.order.email_text || ''
  }
  var savedName = builder.po_number || (builder.customer_info ? builder.customer_info.customer_name : '') || 'ACE Order Entry V2'

  return axios.post(this.$apiAdress + '/api/orders/v2/saveOrderForLater?token=' + localStorage.getItem('api_token'), {
    builder: builder,
    saved_id: activeSavedFields.saved_id || '',
    saved_updated_at: activeSavedFields.saved_updated_at || '',
    save_as_copy: false,
    saved_name: savedName
  }).then((response) => {
    if (response.data && response.data.saved_id) {
      activateSavedDraft2(this.draft.builder, { saved_id: response.data.saved_id, saved_updated_at: response.data.saved_updated_at || '', builder_identity: this.draft.builder.timing_workspace_id })
      return true
    }
    return false
  }).catch((error) => {
    this.$store.commit('axios_error', error)
    return false
  })
}

methods.startNewOrder2 = function () {
  if (!this.draftHasMeaningfulData2()) {
    this.resetGroupReviewState2()
    return
  }

  window.Swal.fire({
    title: 'Start a new order?',
    text: 'Unsaved changes in the current order will be cleared. Saved orders will remain available.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Start New Order',
    confirmButtonColor: '#d9534f',
    cancelButtonText: 'Cancel',
    reverseButtons: true
  }).then(async (result) => {
    if (result.isConfirmed) {
      this.resetGroupReviewState2()
    }
  })
}

methods.persistDraft2 = function () {
  if (!this.draft) {
    return false
  }
  var result = writeVersionedDraft(sessionStorage, ORDER_GROUPS2_DRAFT_KEY, {
    draft: stripEphemeralPreviewUrls(this.draft),
    updated_at: new Date().toISOString()
  }, function (payload) {
    if (!payload || !payload.draft || !Array.isArray(payload.draft.groups)) return false
    var expectedIds = (this.draft.groups || []).reduce(function (ids, group) {
      return ids.concat((group.items || []).map(function (item) { return item.line_id }))
    }, []).filter(Boolean)
    var restoredIds = payload.draft.groups.reduce(function (ids, group) {
      return ids.concat((group.items || []).map(function (item) { return item.line_id }))
    }, []).filter(Boolean)
    return expectedIds.every(function (id) { return restoredIds.indexOf(id) !== -1 })
  }.bind(this))
  this.draftPersistenceResult2 = result
  this.storageInventory2 = storageInventory(sessionStorage, [CREATE_ORDER2_BUILDER_DRAFT_KEY, 'create_order2_group_review', ORDER_GROUPS2_DRAFT_KEY, BUILDER_ISSUE_RETURN_KEY, 'create_order2_collapse_on_return_v1'])
  this.draftPersistenceWarning2 = result.ok ? '' : 'Your changes are still open, but ACE could not save them for refresh recovery. Do not close or refresh this tab.'
  return result.ok
}

methods.queuePersistDraft2 = function () {
  if (this.persistDraftTimer2) {
    clearTimeout(this.persistDraftTimer2)
  }
  this.persistDraftTimer2 = setTimeout(() => {
    this.persistDraft2()
    this.persistDraftTimer2 = null
  }, 200)
}

methods.clearDraft2 = function () {
  sessionStorage.removeItem(ORDER_GROUPS2_DRAFT_KEY)
}

methods.canSubmit2 = function () {
  if (this.eventActive2 && !this.eventAuthorized2) return false
  if (this.draft && this.draft.order && this.draft.order.prebook && !this.prebookCatalogLoaded2) return false
  if (this.shipDateUpdating2 || this.shipDateError2) return false
  if (this.draft && this.draft.builder && this.draft.builder.scheduling_intent && this.draft.builder.scheduling_intent.fast_lane && !this.timingFastLaneConfirmed2) return false
  var builder = this.draft && this.draft.builder ? this.draft.builder : null
  var hasShipTo = !!(builder && (
    builder.ship_to_info ||
    (builder.new_ship_to && builder.new_ship_info && builder.new_ship_info.address_1)
  ))

  if (!builder || !builder.customer_info || !hasShipTo) {
    return false
  }

  if (builder.rep === '' || builder.rep === null) {
    return false
  }

  return (this.draft.groups || []).length > 0 && this.orderIssues2().filter(function (requirement) {
    return requirement.blocksReview && requirement.state !== 'pending'
  }).length === 0
}

methods.isTierProgram2 = function (group) {
  var code = group && group.program_info && group.program_info.price_level_code !== undefined && group.program_info.price_level_code !== null
    ? parseInt(group.program_info.price_level_code, 10) || 0
    : 0
  return code === 2
}

methods.defaultTierForUnits2 = function (units) {
  var totalUnits = parseInt(units || 0, 10) || 0
  if (totalUnits < 72) return '0'
  if (totalUnits >= 72) return '6'
  if (totalUnits >= 36) return '3'
  if (totalUnits >= 24) return '2'
  return '1'
}

methods.ensureGroupTier2 = function (group) {
  if (!group || !this.isTierProgram2(group)) return
  if (group.tier !== undefined && group.tier !== null && group.tier !== '') return
  this.$set(group, 'tier', this.defaultTierForUnits2(group.total_units || 0))
}

methods.normalizeGroupTiers2 = function () {
  (this.draft && this.draft.groups || []).forEach(function (group) {
    this.ensureGroupTier2(group)
  }, this)
}

methods.submitOrder2 = async function () {
  if (this.submitInProgress || !this.canSubmit2()) return

  this.submitInProgress = true
  var submitStage = 'building submit payload'

  try {
    submitStage = 'checking current minimum requirements'
    const minimumPayload = builderMinimumPayload(this.draft.builder, bundlePlacementsForLine)
    const minimumFingerprint = JSON.stringify(minimumPayload)
    const minimumResponse = await axios.post(this.$apiAdress + '/api/orders/v2/minimum-requirements', minimumPayload, withApiToken())
    if (quoteResponseError(minimumResponse) || !minimumResponse.data || minimumResponse.data.version !== MINIMUM_VERSION || minimumResponse.data.blocking !== false || !Array.isArray(minimumResponse.data.requirements) || minimumResponse.data.requirements.some(row => !row || row.blocking !== false) || !Array.isArray(minimumResponse.data.normalizations) || minimumResponse.data.normalizations.length || minimumFingerprint !== JSON.stringify(builderMinimumPayload(this.draft.builder, bundlePlacementsForLine))) {
      throw new Error('Quantity requirements need a current server confirmation. Return to Builder to review quantities.')
    }
    if (this.refreshEventSchedule2 && !(await this.refreshEventSchedule2(false))) throw new Error('Event schedule needs a current confirmation. Check the Event Schedule.')
    if (this.eventActive2 && !this.eventAuthorized2) throw new Error('Event schedule changed. Check the Event Schedule again.')
    const payload = this.buildSubmitPreview2()
    submitStage = 'configuring section totals'
    const anyPromoAlreadyApplied = (payload.order_items || []).some(function (it) {
      return (it.order_item_locations || []).some(function (loc) {
        return !!loc.promotion_applied
      })
    })
    const promoForSubmit = anyPromoAlreadyApplied ? null : null
    const sections = await configureSections(payload.order_items, payload.customer_info, promoForSubmit)
    stampBilledFromSectionsIntoForm2(payload, sections)

    submitStage = 'posting order to backend'
    const response = await axios.post(this.$apiAdress + '/api/orders/v2/submitOrder?token=' + localStorage.getItem('api_token'), payload)
    submitStage = 'processing submit response'
    if (!response || !response.data || response.data.status === 'error' || response.data.status === 'oops' || !response.data.order_id) {
      throw Object.assign(new Error('The server did not confirm that the order was submitted.'), { response: response })
    }
    this.clearDraft2()
    sessionStorage.removeItem(CREATE_ORDER2_BUILDER_DRAFT_KEY)
    sessionStorage.removeItem('create_order2_group_review')

    if (response.data.promotions && response.data.promotions.length) {
      const buttons = {}
      response.data.promotions.forEach(function (promo) {
        const formatted = promo.discount_type
          .replace(/_/g, ' ')
          .replace(/\b\w/g, function (l) { return l.toUpperCase() })
        buttons[promo.token] = {
          text: formatted + ' Promotion',
          value: promo.token
        }
      })

      Swal.fire({
        icon: 'success',
        title: 'Promotion Unlocked!',
        text: 'You’ve qualified for a promotion. Which one would you like to enter?',
        input: 'radio',
        inputOptions: Object.fromEntries(
          Object.entries(buttons).map(function ([token, btn]) { return [token, btn.text] })
        ),
        inputValidator: function (value) {
          if (!value) return 'You need to choose a promotion'
        },
        showCancelButton: true,
        confirmButtonText: 'Let’s go!',
        cancelButtonText: 'I’ll do it later',
        footer: 'You can always come back to it later from Order Actions.'
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          this.$router.push('/create_order/' + result.value + '/promotion')
        } else {
          this.$router.push('/order/' + response.data.order_id)
        }
      })
    } else {
      this.$router.push('/order/' + response.data.order_id)
    }
  } catch (error) {
    this.handleSubmissionFieldIssues2(error)
    error.aceContext = { operation: 'submit_order_' + submitStage.replace(/\s+/g, '_'), savedId: this.draft && this.draft.builder && this.draft.builder.saved_id, activeGroupId: this.activeGroupId2 }
    this.$store.commit('axios_error', error)
  } finally {
    this.submitInProgress = false
  }
}

methods.handleSubmissionFieldIssues2 = function (error) {
  var errors = error && error.response && error.response.data && error.response.data.errors
  if (!errors || typeof errors !== 'object') return false
  var key = Object.keys(errors).find(function (field) { return /^groups\.\d+\.additional_charges$/.test(field) })
  if (!key) return false
  var groupIndex = parseInt(key.split('.')[1], 10)
  var group = this.draft && this.draft.groups && this.draft.groups[groupIndex]
  if (!group) return false
  this.selectGroup2(group, groupIndex)
  this.$set(this.groupPanelState2, this.groupPanelKey2(group, 'charges'), true)
  this.$nextTick(function () {
    scrollAndFocusIssue({ target: 'group_additional_charges_' + group.id }, {
      document: document,
      window: window,
      fallbackTarget: 'group_attention_' + group.id,
      setTimeout: setTimeout
    })
  })
  return true
}

methods.showProgramOptions2 = function (group) {
  var programInfo = group ? group.program_info : null
  if (!programInfo) return true
  return !!(programInfo.rush_option || programInfo.ribbon_option || programInfo.hangtags_option || programInfo.string_tags_option || this.isTierProgram2(group))
}

methods.shouldShowPromotion2 = function (group) {
  if (!group || group.selected_promotion || group.promotion_used) return false
  var prebook = this.draft && this.draft.order ? this.draft.order.prebook : null
  return getEligiblePromotionsForGroup(group, prebook).length > 0
}

methods.applyPromotion2 = function (group, promotion) {
  if (!group) return
  this.$set(group, 'selected_promotion', promotion)
  this.$set(group, 'promotion_used', true)
  this.$set(group, 'showPromoPopup', false)
  this.authoritativeQuoteInputChanged2()
}

methods.syncPromotionVisibility2 = function () {
  (this.draft && this.draft.groups || []).forEach((group) => {
    this.$set(group, 'showPromoPopup', this.shouldShowPromotion2(group))
  })
}

methods.addUploadFile2 = function (group, groupId, file) {
  var files = file ? [file] : []
  if (!group.uploads) {
    this.$set(group, 'uploads', [])
  }

  files.forEach((file) => {
    var reader = new FileReader()
    reader.onload = (loadEvent) => {
      group.uploads.push({
        name: file.name,
        size: file.size,
        type: file.type,
        dataURL: loadEvent.target.result
      })
      var ref = this.$refs['groupUploader_' + groupId]
      if (Array.isArray(ref)) {
        ref = ref[0]
      }
      if (ref && ref.removeFile) {
        ref.removeFile(file)
      }
    }
    reader.readAsDataURL(file)
  })
}

methods.removeUpload2 = function (group, index) {
  if (!group || !group.uploads) return
  group.uploads.splice(index, 1)
}

methods.findSectionForGroup2 = function (group) {
  return (this.draft && this.draft.builder && this.draft.builder.sections || []).find(function (section) {
    return section.id === group.section_id
  }) || null
}

methods.findSectionById2 = function (sectionId) {
  return (this.draft && this.draft.builder && this.draft.builder.sections || []).find(function (section) {
    return section.id === sectionId
  }) || null
}

methods.findLineForGroupItem2 = function (section, groupItem) {
  if (!section) return null
  return (section.style_lines || []).find(function (line) {
    return line.id === groupItem.line_id
  }) || null
}

methods.builderIsDesignFirst2 = function () {
  return true
}

methods.makePreviewLocation2 = function (set, line) {
  var quantity = parseInt(line.units || 0) || 0
  var rawLocation = set && set.location ? set.location : {}
  var artwork = set && set.artwork ? set.artwork : ''
  var isTempUpload = !!(artwork && artwork.temp_upload)
  var normalizedLocation = Object.assign({}, rawLocation, {
    location_id: rawLocation.location_id || rawLocation.id || '',
    location_code: rawLocation.location_code || rawLocation.code || '',
    location_name: rawLocation.location_name || rawLocation.name || ''
  })
  var unitCost = parseFloat(
    (normalizedLocation && (normalizedLocation.technique_cost || normalizedLocation.cost)) ||
    (set.technique && (set.technique.cost || set.technique.technique_cost)) ||
    0
  ) || 0
  var decorationKey = [line && line.id, set && (set.bundle_placement_id || set.id)].filter(Boolean).join(':')

  return {
    id: decorationKey,
    decoration_key: decorationKey,
    bundle_placement_id: set.bundle_placement_id || set.id || '',
    location: normalizedLocation,
    location_name: normalizedLocation.location_name || '',
    location_code: normalizedLocation.location_code || '',
    technique: set.technique || {},
    technique_name: set.technique ? set.technique.technique_name : '',
    technique_internal_id: set.canonical_technique_internal_id || (set.technique && set.technique.internal_id) || null,
    location_internal_id: set.canonical_location_internal_id || normalizedLocation.internal_id || null,
    product_class: set.selected_product_class || normalizedLocation.product_class || (line.style_info && line.style_info.product_class) || null,
    resolution_source: set.resolution_source || '',
    manual_confirmation: set.resolution_source === 'manual',
    configuration_key: set.configuration_key || '',
    ptl_identity: set.ptl_identity || normalizedLocation.ptl_identity || '',
    ptl_program_id: set.ptl_program_id || normalizedLocation.ptl_program_id || null,
    ptl_technique_id: set.ptl_technique_id || normalizedLocation.ptl_technique_id || null,
    ptl_location_id: set.ptl_location_id || normalizedLocation.ptl_location_id || null,
    selected_configuration_label: [set.technique && set.technique.technique_name, normalizedLocation.location_code || normalizedLocation.location_name].filter(Boolean).join(' / '),
    decoration: isTempUpload ? '' : (artwork || ''),
    flow_image: isTempUpload ? '' : (artwork || ''),
    file_name: set.artwork_name || '',
    file_url: '',
    decoration_text: set.decoration_text || '',
    text_decoration: set.design_mode === 'text_only' ? 1 : 0,
    layout_text: '',
    color_type: typeof set.color_type === 'string' ? set.color_type : '',
    proof_required: 'No',
    new_decoration: set.design_mode === 'new_design' ? 1 : 0,
    new_decoration_data: set.design_mode === 'new_design' ? (artwork || '') : '',
    blank_order: 0,
    quantity: quantity,
    source_cost: unitCost.toFixed(2),
    cost: unitCost.toFixed(2),
    technique_cost: +(unitCost * quantity).toFixed(2),
    one_to_23_upcharge_cost: 0
  }
}

methods.computePreviewStylePrice2 = function (line) {
  var total = 0

  function sumColor(color) {
    (color.sizes || []).forEach(function (size) {
      var quantity = parseInt(size.quantity || 0) || 0
      var unitPrice = parseFloat(size.unit_price || 0) || 0
      total += quantity * unitPrice
    })
    ;(color.inner_colors || []).forEach(function (innerColor) {
      sumColor(innerColor)
    })
  }

  (line.colors || []).forEach(function (color) {
    sumColor(color)
  })

  return +total.toFixed(2)
}

methods.resolvePreviewSizeUnitPrice2 = function (size, item, group) {
  var existing = size && size.unit_price
  if (existing !== undefined && existing !== null && existing !== '') {
    return parseFloat(existing) || 0
  }

  var pricingCode = this.draft && this.draft.builder && this.draft.builder.customer_info
    ? this.draft.builder.customer_info.pricing_level_code
    : ''
  pricingCode = pricingCode ? String(pricingCode).padStart(2, '0') : ''

  var isBlank = parseInt(item && item.blank_order ? item.blank_order : 0) === 1
  var programPriceLevelCode = item && item.program_info && item.program_info.price_level_code !== undefined && item.program_info.price_level_code !== null
    ? parseInt(item.program_info.price_level_code, 10) || 0
    : 0

  if (!isBlank && programPriceLevelCode === 2) {
    var tier = group && group.tier !== undefined && group.tier !== null && group.tier !== ''
      ? String(group.tier)
      : ''

    var tierMap = {
      '1': pricingCode === '09' ? 'distr_min_12' : 'price_min_12',
      '2': pricingCode === '09' ? 'distr_min_24' : 'price_min_24',
      '3': pricingCode === '09' ? 'distr_min_36' : 'price_min_36',
      '6': pricingCode === '09' ? 'distr_min_72' : 'price_min_72'
    }

    var tierKey = tierMap[tier]
    if (tierKey && size && size[tierKey] !== undefined && size[tierKey] !== null && size[tierKey] !== '') {
      return parseFloat(size[tierKey]) || 0
    }
  }

  if (isBlank) {
    if (pricingCode === '09') {
      var blankDist = size && size.blank_dist !== undefined && size.blank_dist !== null && size.blank_dist !== ''
        ? parseFloat(size.blank_dist) || 0
        : null
      if (blankDist !== null) {
        return blankDist
      }
    }

    var blank = size && size.blank !== undefined && size.blank !== null && size.blank !== ''
      ? parseFloat(size.blank) || 0
      : null
    if (blank !== null) {
      return blank
    }
  }

  if (pricingCode) {
    var priceKey = 'price_' + pricingCode
    if (size && size[priceKey] !== undefined && size[priceKey] !== null && size[priceKey] !== '') {
      return parseFloat(size[priceKey]) || 0
    }
  }

  return parseFloat(size && size.cost ? size.cost : 0) || 0
}

methods.normalizePreviewColors2 = function (line, item, group) {
  var self = this
  var colors = clone2(line.colors || [])

  function normalizeColor(color) {
    color.sizes = (color.sizes || []).map(function (size) {
      size.unit_price = self.resolvePreviewSizeUnitPrice2(size, item, group)
      return size
    })
    color.inner_colors = (color.inner_colors || []).map(function (innerColor) {
      return normalizeColor(innerColor)
    })
    return color
  }

  return colors.map(function (color) {
    return normalizeColor(color)
  })
}

methods.computePreviewTechniqueTotal2 = function (locations) {
  return +((locations || []).reduce(function (sum, location) {
    return sum + (parseFloat(location.technique_cost || 0) || 0)
  }, 0)).toFixed(2)
}

methods.piecesForPreviewItem2 = function (item) {
  var total = 0
  ;((item && item.order_item_colors) || []).forEach(function (color) {
    (color.sizes || []).forEach(function (size) {
      total += parseInt(size.quantity || 0) || 0
    })
    ;(color.inner_colors || []).forEach(function (innerColor) {
      (innerColor.sizes || []).forEach(function (size) {
        total += parseInt(size.quantity || 0) || 0
      })
    })
  })
  return total
}

methods.previewLocationBucketKey2 = function (location) {
  if (!location || !location.technique || !location.location) {
    return ''
  }

  var base = (location.technique.technique_name || '') + '_' + (location.location.location_code || '')
  var useLocationOnly = location.location.location_for_free_decoration_matters
  if (useLocationOnly) {
    return base
  }

  var artName = ''
  if (location.decoration && location.decoration.name) {
    artName = location.decoration.name
  } else if (location.new_decoration_data && location.new_decoration_data.name) {
    artName = location.new_decoration_data.name
  } else if (location.flow_image && location.flow_image.name) {
    artName = location.flow_image.name
  } else if (location.file_name) {
    artName = location.file_name
  }

  var parts = [base, String(artName || '').replace(/[ ,.-]/g, '_')]
  if (location.decoration_text) {
    parts.push(String(location.decoration_text).replace(/[ ,.-]/g, '_'))
  }
  if (location.layout_text) {
    parts.push(String(location.layout_text).replace(/[ ,.-]/g, '_'))
  }
  return parts.filter(Boolean).join('_')
}

methods.previewLocationIsNewArt2 = function (location) {
  return !!(
    location &&
    (
      location.new_decoration ||
      location.new_decoration_data ||
      (location.flow_image && location.flow_image.req_type && location.flow_image.req_type !== 'embroideries' && location.flow_image.req_type !== 'components')
    )
  )
}

methods.previewLocationMinTarget2 = function (location) {
  if (!location || !location.technique) {
    return 0
  }
  if (this.previewLocationIsNewArt2(location) && location.technique.new_min_quantity) {
    return parseInt(location.technique.new_min_quantity || 0) || 0
  }
  return parseInt(location.technique.min_quantity || 0) || 0
}

methods.previewLocationUnitTechniqueCost2 = function (location) {
  if (!location) {
    return 0
  }
  if (location.source_cost !== undefined && location.source_cost !== null && location.source_cost !== '') {
    return parseFloat(location.source_cost || 0) || 0
  }
  if (location.cost !== undefined && location.cost !== null && location.cost !== '') {
    return parseFloat(location.cost || 0) || 0
  }
  if (this.draft && this.draft.builder && this.draft.builder.customer_info && this.draft.builder.customer_info.pricing_level_code === '09') {
    return parseFloat(location.technique && location.technique.distributor_cost ? location.technique.distributor_cost : 0) || 0
  }
  return parseFloat(location.technique && location.technique.cost ? location.technique.cost : 0) || 0
}

methods.evaluatePreviewGroup2 = function (group, orderItems) {
  var warnings = []
  var usage = {}
  var styleTotal = 0
  var techniqueTotal = 0

  ;(orderItems || []).forEach(function (item, itemIndex) {
    styleTotal += parseFloat(item.price || 0) || 0
    var itemPieces = this.piecesForPreviewItem2(item)
    var sourceMeta = (group.items || [])[itemIndex] || {}

    ;(item.order_item_locations || []).forEach(function (location) {
      var bucketKey = this.previewLocationBucketKey2(location)
      if (!bucketKey) {
        return
      }
      if (!usage[bucketKey]) {
        usage[bucketKey] = {
          count: 0,
          total_quantity: 0,
          free_decorations: parseInt(location.location && location.location.location_for_free_decoration_matters
            ? (location.location.free_decorations || 0)
            : (location.technique && location.technique.free_decorations ? location.technique.free_decorations : 0), 10) || 0,
          minimum: this.previewLocationMinTarget2(location),
          under_min_quantity_upcharge: parseFloat(location.technique && location.technique.under_min_quantity_upcharge ? location.technique.under_min_quantity_upcharge : 0) || 0,
          description: (location.technique && location.technique.technique_name ? location.technique.technique_name : 'Technique') + ' @ ' + (location.location && (location.location.location_code || location.location.location_name) ? (location.location.location_code || location.location.location_name) : 'Location'),
          sections: {}
        }
      }

      usage[bucketKey].count += 1
      usage[bucketKey].total_quantity += itemPieces
      if (sourceMeta.source_section_name) {
        usage[bucketKey].sections[sourceMeta.source_section_name] = true
      }

      var freeDecorations = usage[bucketKey].free_decorations
      var unitTechniqueCost = this.previewLocationUnitTechniqueCost2(location)
      var isFreeDecoration = freeDecorations > 0 && usage[bucketKey].count <= freeDecorations
      var locationTechCost = isFreeDecoration ? 0 : parseFloat((unitTechniqueCost * itemPieces).toFixed(2))

      location.cost = isFreeDecoration ? 0 : unitTechniqueCost
      location.technique_cost = locationTechCost
      location.free_decoration_applied = isFreeDecoration
      techniqueTotal += locationTechCost
    }, this)

    item.technique_cost = this.computePreviewTechniqueTotal2(item.order_item_locations || [])
    item.total_price = +((parseFloat(item.price || 0) || 0) + (parseFloat(item.technique_cost || 0) || 0)).toFixed(2)
  }, this)

  Object.keys(usage).forEach(function (bucketKey) {
    var bucket = usage[bucketKey]
    if (bucket.minimum > 0 && bucket.total_quantity > 0 && bucket.total_quantity < bucket.minimum) {
      var sections = Object.keys(bucket.sections)
      warnings.push({
        type: 'minimum',
        message: bucket.description + ' is under minimum: ' + bucket.total_quantity + ' of ' + bucket.minimum + (sections.length ? ' in ' + sections.join(', ') : '') + '.'
      })
    }
  })

  return {
    order_items: orderItems,
    style_total: +styleTotal.toFixed(2),
    technique_total: +techniqueTotal.toFixed(2),
    group_total: +(styleTotal + techniqueTotal).toFixed(2),
    warnings: warnings
  }
}

methods.lineBundlePlacementsDraft2 = function (section, line) {
  if (isBlankSection(section)) return []
  return bundlePlacementsForLine(section, line)
}

methods.makePreviewOrderItem2 = function (section, line, group) {
  var attachedSets = isBlankSection(section) ? [] : this.builderIsDesignFirst2(this.draft && this.draft.builder)
    ? this.lineBundlePlacementsDraft2(section, line)
    : (section.decoration_sets || []).filter(function (set) {
        return (line.decoration_set_ids || []).indexOf(set.id) > -1
      })
  var locations = attachedSets.map(function (set) {
    return this.makePreviewLocation2(set, line)
  }, this)
  var previewItem = {
    blank_order: isBlankSection(section) ? 1 : 0,
    section_type: section.section_type,
    program_info: section.program_info || {}
  }
  var colors = this.normalizePreviewColors2(line, previewItem, group)
  var pricingLine = Object.assign({}, line, {
    colors: colors
  })
  var stylePrice = this.computePreviewStylePrice2(pricingLine)
  var techniqueTotal = this.computePreviewTechniqueTotal2(locations)
  var units = parseInt(line.units || 0) || 0

  previewItem = {
    id: line.id || null,
    section_reference: section.id || section.name || null,
    section_id: section.id || '',
    line_id: line.id || '',
    style_info: line.style_info || {},
    style_name: line.style_info ? line.style_info.style_name : '',
    style_description: line.style_info ? line.style_info.style_description : '',
    program_info: section.program_info || {},
    program_name: section.program_info ? section.program_info.program_name : '',
    license: section.license || '',
    license_design: section.license_design || '',
    blank_order: isBlankSection(section) ? 1 : 0,
    section_type: section.section_type,
    special_instructions: line.notes || '',
    units: units,
    unit_price: units ? +(stylePrice / units).toFixed(2) : 0,
    price: stylePrice,
    total_price: +(stylePrice + techniqueTotal).toFixed(2),
    technique_cost: techniqueTotal,
    upcharge_cost: 0,
    upcharge_price: 0,
    upcharge_pieces: 0,
    combined_upcharge_cost: 0,
    combined_upcharge_price: 0,
    under_min_quantity_upcharge: 0,
    one_to_23_upcharge_cost: 0,
    one_to_23_upcharge_price: 0,
    one_to_23_upcharge_units: 0,
    order_item_colors: colors,
    order_item_locations: locations
  }

  return previewItem
}

methods.previewFormForGroup2 = function (group) {
  var orderItems = []

  ;(group.items || []).forEach(function (groupItem) {
    var section = this.findSectionById2(groupItem.section_id)
    var line = this.findLineForGroupItem2(section, groupItem)
    if (!line) return
    orderItems.push(this.makePreviewOrderItem2(section, line, group))
  }, this)

  var evaluation = this.evaluatePreviewGroup2(group, orderItems)
  var quoteGroup = this.quoteGroupFor2(group)
  applyAuthoritativeQuoteToPreview2(evaluation.order_items, quoteGroup)

  return {
    order_id: '',
    cc_number: '',
    as400_log_number: '',
    local_created_at: this.draft.created_at || '',
    po_number: this.draft.builder.po_number || '',
    wait_on_customer_po: this.draft.builder.wait_on_customer_po === true || this.draft.builder.wait_on_customer_po === 1 || this.draft.builder.wait_on_customer_po === '1' ? 1 : 0,
    customer_info: this.draft.builder.customer_info || {},
    ship_to_info: this.draft.builder.ship_to_info || {},
    new_ship_to: this.draft.builder.new_ship_to ? 1 : 0,
    new_ship_info: this.draft.builder.new_ship_info || {},
    rep: this.draft.builder.rep || {},
    in_hands_date: this.draft.order.in_hands_date || '',
    future_ship_date: this.draft.order.future_ship_date || '',
    ship_date: group.ship_date || '',
    fast_lane: group.fast_lane ? 1 : 0,
    program_info: group.program_info || {},
    ship_via: group.ship_via || '',
    ship_via_code: group.ship_via ? group.ship_via.ship_via_code : '',
    ship_via_text: group.ship_via ? group.ship_via.ship_via_text : '',
    prebook: clone2(this.normalizePrebookOption2(this.draft.order.prebook)),
    rush: group.rush ? 1 : 0,
    ribbon: group.ribbon ? 1 : 0,
    hangtags: group.hangtags ? 1 : 0,
    string_tags: group.string_tags ? 1 : 0,
    proof_required: 'No',
    special_instructions: group.special_instructions || '',
    additional_charges: clone2(group.additional_charges || []),
    royalty_charges: clone2(group.royalty_charges || []),
    selected_promotion: group.selected_promotion || null,
    tier: group.tier || '',
    one_to_23_upcharge_cost: quoteGroup && quoteGroup.compatibility_adjustments ? quoteGroup.compatibility_adjustments.one_to_23_upcharge_cost : 0,
    one_to_23_upcharge_pieces: quoteGroup && quoteGroup.compatibility_adjustments ? quoteGroup.compatibility_adjustments.one_to_23_upcharge_pieces : 0,
    one_to_23_upcharge_price: quoteGroup && quoteGroup.compatibility_adjustments ? quoteGroup.compatibility_adjustments.one_to_23_upcharge_price : 0,
    order_items: evaluation.order_items,
    style_total: evaluation.style_total,
    technique_total: quoteGroup ? Number(quoteGroup.totals.decoration_subtotal || 0) + Number(quoteGroup.totals.additional_locations || 0) : evaluation.technique_total,
    preview_group_total: quoteGroup ? quoteGroup.totals.quoted_group_total : evaluation.group_total,
    warnings: evaluation.warnings
  }
}

methods.syncGroupEvaluation2 = function (group) {
  if (!group) {
    return null
  }
  var preview = this.previewFormForGroup2(group)
  var evaluation = {
    style_total: parseFloat(preview.style_total || 0) || 0,
    technique_total: parseFloat(preview.technique_total || 0) || 0,
    group_total: parseFloat(preview.preview_group_total || 0) || 0,
    warnings: preview.warnings || []
  }
  var current = group.evaluation ? JSON.stringify(group.evaluation) : ''
  var next = JSON.stringify(evaluation)
  if (current !== next) {
    this.$set(group, 'evaluation', evaluation)
  }
  return evaluation
}

methods.refreshGroupEvaluations2 = function () {
  (this.draft && this.draft.groups || []).forEach(function (group) {
    this.syncGroupEvaluation2(group)
  }, this)
}

methods.queueRefreshGroupEvaluations2 = function () {
  if (this.refreshGroupEvaluationsTimer2) {
    clearTimeout(this.refreshGroupEvaluationsTimer2)
  }
  this.refreshGroupEvaluationsTimer2 = setTimeout(() => {
    this.refreshGroupEvaluations2()
    this.refreshGroupEvaluationsTimer2 = null
  }, 150)
}

methods.makeAvailableEmails2 = function (builder) {
  var emails = []
  var seen = {}

  function pushEmail(email, shipToEmail) {
    if (!email) return
    var key = String(email).trim().toLowerCase()
    if (!key || seen[key]) return
    seen[key] = true
    emails.push({
      checked: false,
      email: email,
      ship_to_email: !!shipToEmail
    })
  }

  pushEmail(builder.customer_info ? builder.customer_info.customer_email : '', false)
  pushEmail(builder.ship_to_info ? builder.ship_to_info.email : '', true)
  pushEmail(builder.new_ship_info ? builder.new_ship_info.email : '', true)

  return emails
}

methods.isDomesticProgramName2 = function (programName) {
  return String(programName || '').trim().toLowerCase() === 'domestic'
}

methods.normalizeSplitOrderKey2 = function (value) {
  value = String(value || '').trim().toLowerCase()
  return value === '' ? null : value
}

methods.getItemSplitOrderKey2 = function (item) {
  var keys = {}
  var hasNull = false

  ;((item && item.order_item_locations) || []).forEach(function (location) {
    var splitOrderKey = this.normalizeSplitOrderKey2(location && location.technique ? location.technique.split_order_key : '')
    if (splitOrderKey === null) {
      hasNull = true
    } else {
      keys[splitOrderKey] = true
    }
  }, this)

  var values = Object.keys(keys)
  if (values.length === 1 && !hasNull && values[0] === 'embroidery_only') {
    return 'embroidery_only'
  }
  if (hasNull || values.length > 0) {
    return '__non_embroidery__'
  }
  return null
}

methods.sanitizeGroupNamePart2 = function (value) {
  return String(value || '').replace(/[-.,\s]/g, '_')
}

methods.frontendGroupName2 = function (item, index) {
  var programName = item && item.program_info ? item.program_info.program_name : ''
  var shipDate = item && item.ship_date ? item.ship_date : ''
  var eventDate = item && item.event_date ? item.event_date : ''
  var isDomestic = this.isDomesticProgramName2(programName)
  var name = programName + '_' + shipDate + '_' + eventDate

  if (parseInt(item && item.program_info ? item.program_info.is_licensed : 0, 10)) {
    if (item && item.license && item.license.name) {
      name += '_' + item.license.name
    }
  } else if (!isDomestic && parseInt(item && item.program_info ? item.program_info.price_level_code : 0, 10) !== 1) {
    ((item && item.order_item_locations) || []).forEach(function (location) {
      if (location && location.location && location.location.location_for_free_decoration_matters === false) {
        var decorationName = ''
        if (location.decoration && location.decoration.name) {
          decorationName = this.sanitizeGroupNamePart2(location.decoration.name)
        } else if (location.new_decoration_data && location.new_decoration_data.name) {
          decorationName = location.new_decoration_data.name
        } else if (location.flow_image && location.flow_image.name) {
          decorationName = this.sanitizeGroupNamePart2(location.flow_image.name)
        } else if (location.file_name) {
          decorationName = this.sanitizeGroupNamePart2(location.file_name)
        }
        name += '_' + (location.location.location_code || '') + '_' + decorationName
        if (location.decoration_text) {
          name += '_' + this.sanitizeGroupNamePart2(location.decoration_text)
        }
        if (location.layout_text) {
          name += '_' + this.sanitizeGroupNamePart2(location.layout_text)
        }
      } else if (location && location.technique && location.location) {
        name = (location.technique.technique_name || '') + '_' + (location.location.location_code || '')
      }
    }, this)
  }

  var itemSplitOrderKey = isDomestic ? this.getItemSplitOrderKey2(item) : null
  if (itemSplitOrderKey) {
    name = programName + '_' + shipDate + '_' + eventDate + '_' + itemSplitOrderKey
    if (parseInt(item && item.program_info ? item.program_info.is_licensed : 0, 10) && item && item.license && item.license.name) {
      name += '_' + item.license.name
    }
  }

  if (parseInt(item && item.program_info ? item.program_info.one_style_per : 0, 10)) {
    name += 'one_per_' + index
  }

  return name
}

methods.attachLegacySourceRefs2 = function (builder, orderItems) {
  var lineRefs = []
  ;(builder.sections || []).forEach(function (section, sectionIndex) {
    (section.style_lines || []).forEach(function (line, lineIndex) {
      if (!line.style_info || !line.style_info.style_id) {
        return
      }
      lineRefs.push({
        section_id: section.id,
        line_id: line.id,
        temp_section_name: section.name || ('Section ' + (sectionIndex + 1)),
        style_number: lineIndex + 1
      })
    })
  })

  return (orderItems || []).map(function (item, index) {
    if (item.section_id && item.line_id) {
      return item
    }
    var ref = lineRefs[index] || {}
    item.section_id = item.section_id || ref.section_id || ''
    item.line_id = item.line_id || ref.line_id || ''
    item.temp_section_name = item.temp_section_name || ref.temp_section_name || ''
    return item
  })
}

methods.buildMergedGroupName2 = function (sourceSections, fallbackKey) {
  var names = (sourceSections || []).map(function (section) {
    return section && section.name ? section.name : ''
  }).filter(Boolean)

  var uniqueNames = Array.from(new Set(names))
  if (!uniqueNames.length) {
    return fallbackKey || 'Merged Group'
  }
  if (uniqueNames.length === 1) {
    return uniqueNames[0]
  }
  return uniqueNames[0] + ' +' + (uniqueNames.length - 1) + ' more'
}

methods.defaultGroupInstructions2 = function (sourceSections) {
  return Array.from(new Set((sourceSections || []).map(function (section) {
    return section && section.notes ? String(section.notes).trim() : ''
  }).filter(Boolean))).join('\n')
}

methods.reconcileSavedGroups2 = function (groups, savedGroups) {
  var savedByKey = {}
  ;(savedGroups || []).forEach(function (group) {
    savedByKey[group.group_key || group.id] = group
  })

  return (groups || []).map(function (group, index) {
    const baseKey = String(group.group_key).replace(/:merchandise:(blank|decorated)$/, '')
    const candidates = groups.filter(candidate => String(candidate.group_key).replace(/:merchandise:(blank|decorated)$/, '') === baseKey)
    var saved = savedByKey[group.group_key] || (candidates.length === 1 ? savedByKey[baseKey] : null)
    if (!saved) {
      group.expanded = index === 0
      return group
    }

    return Object.assign({}, group, {
      id: saved.id || group.id,
      expanded: saved.expanded !== undefined ? saved.expanded : index === 0,
      show_pdf_preview: !!saved.show_pdf_preview,
      ship_via: saved.ship_via || group.ship_via,
      wait_on_customer: saved.wait_on_customer ? 1 : 0,
      special_instructions: saved.special_instructions !== undefined ? saved.special_instructions : group.special_instructions,
      rush: saved.rush ? 1 : 0,
      ribbon: saved.ribbon ? 1 : 0,
      hangtags: saved.hangtags ? 1 : 0,
      string_tags: saved.string_tags ? 1 : 0,
      fast_lane: saved.fast_lane ? 1 : 0,
      uploads: clone2(saved.uploads || []),
      additional_charges: clone2(saved.additional_charges || []),
      royalty_charges: clone2(saved.royalty_charges || []),
      selected_promotion: saved.selected_promotion || null,
      tier: saved.tier || group.tier,
      promotion_used: !!saved.promotion_used,
      showPromoPopup: !!saved.showPromoPopup
    })
  })
}

methods.makeGroupsFromBuilder2 = async function (builder, legacy, savedDraft) {
  var legacyItems = this.attachLegacySourceRefs2(builder, clone2((legacy && legacy.order_items) || []))
  if (!legacyItems.length) {
    return []
  }

  var groupedItems = {}
  legacyItems.forEach(function (item, index) {
    var groupKey = v2MerchandiseGroupKey(this.frontendGroupName2(item, index), item)
    if (!groupedItems[groupKey]) {
      groupedItems[groupKey] = []
    }
    groupedItems[groupKey].push(item)
  }, this)

  var groups = Object.keys(groupedItems).map(function (groupKey, index) {
    var mergedItems = groupedItems[groupKey]
    var firstItem = mergedItems[0] || {}
    var sourceSectionIds = Array.from(new Set((mergedItems || []).map(function (item) {
      return item.section_id
    }).filter(Boolean)))
    var sourceSections = sourceSectionIds.map(function (sectionId) {
      return (builder.sections || []).find(function (section) {
        return section.id === sectionId
      }) || null
    }).filter(Boolean)
    var designTypes = {}
    ;(mergedItems || []).forEach(function (item) {
      ((item && item.order_item_locations) || []).forEach(function (location) {
        var key = this.normalizeSplitOrderKey2(location && location.technique ? location.technique.split_order_key : '')
        if (key) {
          designTypes[key] = true
        }
      }, this)
    }, this)

    var groupDesignType = null
    var designTypeKeys = Object.keys(designTypes)
    if (designTypeKeys.length === 1) {
      groupDesignType = designTypeKeys[0]
    } else if (designTypeKeys.length > 1) {
      groupDesignType = 'technique_combination'
    }


    return {
      id: uid2('group2'),
      group_key: groupKey,
      expanded: index === 0,
      show_pdf_preview: false,
      section_id: sourceSectionIds[0] || '',
      source_section_ids: sourceSectionIds,
      source_group_ids: Array.from(new Set(sourceSections.map(section => section.source_group_id).filter(Boolean))),
      name: this.buildMergedGroupName2(
        sourceSections,
        sourceSections.length === 1
          ? 'Style Group ' + ((builder.sections || []).indexOf(sourceSections[0]) + 1)
          : 'Group ' + (index + 1)
      ),
      program_info: firstItem.program_info || '',
      license: firstItem.license || '',
      license_design: firstItem.license_design || '',
      design_type: groupDesignType,
      ship_date: '',
      ship_via: '',
      wait_on_customer: false,
      special_instructions: this.defaultGroupInstructions2(sourceSections),
      rush: 0,
      ribbon: 0,
      hangtags: 0,
      string_tags: 0,
      fast_lane: 0,
      uploads: [],
      additional_charges: [],
      royalty_charges: [],
      selected_promotion: null,
      tier: '',
      promotion_used: false,
      showPromoPopup: false,
      items: (mergedItems || []).map(function (item) {
        var sourceSection = sourceSections.find(function (section) {
          return section.id === item.section_id
        })
        var sourceLine = sourceSection
          ? (sourceSection.style_lines || []).find(function (line) { return line.id === item.line_id }) || null
          : null
        return {
          section_id: item.section_id || '',
          line_id: item.line_id || '',
          style_id: item.style_info ? item.style_info.style_id : '',
          style_name: item.style_info ? item.style_info.style_name : '',
          style_description: item.style_info ? item.style_info.style_description : '',
          units: parseInt(item.units || 0) || 0,
          bundle_id: sourceSection && sourceSection.bundle_id ? sourceSection.bundle_id : '',
          decoration_set_ids: clone2(sourceLine && sourceLine.decoration_set_ids ? sourceLine.decoration_set_ids : []),
          source_section_name: item.temp_section_name || (sourceSection && sourceSection.name) || ''
        }
      }),
      total_units: (mergedItems || []).reduce(function (sum, item) {
        return sum + (parseInt(item.units || 0) || 0)
      }, 0)
    }
  }, this)

  if (savedDraft && savedDraft.groups) {
    builder.blank_recovery = builder.blank_recovery || []
    savedDraft.groups.forEach(saved => {
      const key = saved.group_key || saved.id
      const partitions = groups.filter(group => group.group_key.replace(/:merchandise:(blank|decorated)$/, '') === key)
      if (partitions.length < 2) return
      ;['additional_charges', 'royalty_charges', 'selected_promotion', 'tier'].forEach(field => {
        const value = saved[field]
        const id = 'review-' + key + '-' + field
        if (value && (!Array.isArray(value) || value.length) && !builder.blank_recovery.some(row => row.id === id)) builder.blank_recovery.push({ id, source_group_id: saved.id, field, value: JSON.stringify(value), status: 'pending' })
      })
    })
    groups = this.reconcileSavedGroups2(groups, savedDraft.groups)
  }

  return groups
}

methods.loadDraft2 = async function (bootstrapGeneration) {
  this.missingDraft = false
  var raw = sessionStorage.getItem('create_order2_group_review')
  var savedDraft = sessionStorage.getItem(ORDER_GROUPS2_DRAFT_KEY)
  var parsedSavedDraft = null
  if (savedDraft) {
    try {
      parsedSavedDraft = JSON.parse(savedDraft)
      if (parsedSavedDraft && parsedSavedDraft.schema_version) parsedSavedDraft = parsedSavedDraft.payload
    } catch (e) {
      // Ignore malformed browser-only review state and rebuild from the builder draft.
    }
  }
  if (!raw) {
    if (parsedSavedDraft && parsedSavedDraft.draft) {
      this.draft = parsedSavedDraft.draft
      this.normalizeHydratedDraft2()
      this.draft.builder.entry_mode = 'design_first'
      delete this.draft.builder.order_notes
      this.normalizeGroupTiers2()
      if (this.prebookCatalogLoaded2) this.draft.order.prebook = this.normalizePrebookOption2(this.draft.order.prebook)
      this.syncFastLaneAcrossGroups2()
      this.syncPromotionVisibility2()
      this.refreshGroupEvaluations2()
      return true
    }
    this.missingDraft = true
    return false
  }

  try {
    var parsedHandoff = JSON.parse(raw)
    if (parsedHandoff && parsedHandoff.schema_version) parsedHandoff = parsedHandoff.payload
    var stored = parsedHandoff && parsedHandoff.handoff_version
      ? restoreGroupReviewHandoff2(parsedHandoff)
      : parsedHandoff
    var builder = stored.builder || {}
    initializeTiming(builder, parsedSavedDraft)
    builder.entry_mode = 'design_first'
    delete builder.order_notes
    var groups = await this.makeGroupsFromBuilder2(builder, stored.legacy || {}, parsedSavedDraft && parsedSavedDraft.draft ? parsedSavedDraft.draft : null)
    if (bootstrapGeneration !== undefined && bootstrapGeneration !== this.pageBootstrapGeneration2) return false
    var importedOrder = builder.imported_review_order && typeof builder.imported_review_order === 'object' ? builder.imported_review_order : {}

    this.draft = {
      builder: builder,
      legacy: stored.legacy || {},
      created_at: stored.created_at || '',
      order: parsedSavedDraft && parsedSavedDraft.draft && parsedSavedDraft.draft.order
        ? clone2(parsedSavedDraft.draft.order)
        : {
        available_emails: Array.isArray(importedOrder.available_emails) && importedOrder.available_emails.length ? clone2(importedOrder.available_emails) : this.makeAvailableEmails2(builder),
        extra_emails: importedOrder.extra_emails || '',
        email_text: importedOrder.email_text || '',
        event_order: false,
        in_hands_date: '',
        future_ship_date_boolean: false,
        future_ship_date: '',
        prebook: builder.prebook || importedOrder.prebook || '',
        fast_lane: 0
      },
      groups: groups
    }
    this.normalizeHydratedDraft2()

    if (!this.draft.order.available_emails || !this.draft.order.available_emails.length) {
      this.draft.order.available_emails = this.makeAvailableEmails2(builder)
    }

    this.normalizeGroupTiers2()
    if (this.prebookCatalogLoaded2) this.draft.order.prebook = this.normalizePrebookOption2(this.draft.order.prebook)
    this.syncFastLaneAcrossGroups2()
    this.syncPromotionVisibility2()
    this.refreshGroupEvaluations2()
    return true
  } catch (e) {
    throw e
  }
}

methods.makeReviewOrderDefaults2 = function (builder) {
  return {
    available_emails: this.makeAvailableEmails2(builder || {}),
    extra_emails: '',
    email_text: '',
    event_order: false,
    in_hands_date: '',
    future_ship_date_boolean: false,
    future_ship_date: '',
    prebook: '',
    fast_lane: 0
  }
}

methods.normalizeHydratedDraft2 = function () {
  if (!this.draft) return
  this.timingFastLaneConfirmed2 = false
  this.draft.builder = this.draft.builder || {}
  ;(this.draft.builder.sections || []).forEach(section => restoreSectionIntent(section, this.draft.builder.bundles || []))
  this.draft.groups = Array.isArray(this.draft.groups) ? this.draft.groups : []
  this.draft.order = Object.assign(
    this.makeReviewOrderDefaults2(this.draft.builder),
    this.draft.order || {}
  )
  if (Object.prototype.hasOwnProperty.call(this.draft.builder, 'prebook')) this.draft.order.prebook = clone2(this.draft.builder.prebook || '')
  initializeTiming(this.draft.builder, { draft: this.draft })
  this.syncBuilderTiming2()
  if (!Array.isArray(this.draft.order.available_emails)) {
    this.draft.order.available_emails = this.makeAvailableEmails2(this.draft.builder)
  }
}

methods.initializeQuoteFlow2 = async function () {
  var bootstrapGeneration = ++this.pageBootstrapGeneration2
  this.quoteHydrating = true
  this.pageBootstrapState2 = 'initializing'
  this.pageBootstrapReference2 = ''
  var loaded = false
  try {
    loaded = await this.loadDraft2(bootstrapGeneration)
    if (bootstrapGeneration !== this.pageBootstrapGeneration2) return false
    if (!loaded) {
      this.pageBootstrapState2 = 'redirecting'
      await this.$router.replace({ path: '/create_order2' })
      return false
    } else {
      this.initializeActiveGroup2()
      this.pageBootstrapState2 = 'ready'
      this.loadPrebookOptions2()
      if (this.eventActive2) this.refreshEventSchedule2(false)
    }
    return true
  } catch (error) {
    if (bootstrapGeneration !== this.pageBootstrapGeneration2) return false
    var response = error && error.response && error.response.data ? error.response.data : {}
    this.pageBootstrapReference2 = response.reference || response.correlation_id || ''
    this.pageBootstrapState2 = 'failed'
    return false
  } finally {
    if (bootstrapGeneration !== this.pageBootstrapGeneration2) return
    this.quoteHydrating = false
    if (loaded && this.draft && this.pageBootstrapState2 === 'ready') {
      this.persistDraft2()
      this.queueShipDateRefresh2(true)
      if (!this.quoteInitialScheduled2 && this.queueQuoteRefresh2({ immediate: true })) {
        this.quoteInitialScheduled2 = true
      }
      window.addEventListener('beforeunload', this.persistDraft2)
    }
  }
}

methods.syncFastLaneAcrossGroups2 = function () {
  var fastLane = this.draft && this.draft.order ? (this.draft.order.fast_lane ? 1 : 0) : 0
  ;(this.draft.groups || []).forEach(function (group) {
    group.fast_lane = fastLane
  })
}

methods.buildSubmitPreview2 = function () {
  if (!this.draft) {
    return {}
  }

  if (this.syncBuilderTiming2) this.syncBuilderTiming2()
  var checkedEmails = (this.draft.order.available_emails || []).filter(function (row) {
    return !!row.checked
  }).map(function (row) {
    return row.email
  })
  var evaluatedOrderItems = []
  ;(this.draft.groups || []).forEach(function (group) {
    var preview = this.previewFormForGroup2(group)
    evaluatedOrderItems = evaluatedOrderItems.concat(clone2(preview.order_items || []))
  }, this)

  var builderSnapshot = clone2(this.draft.builder || {})
  var savedFields = savedDraftSubmissionFields2(builderSnapshot)
  if (!savedFields.saved_id) {
    delete builderSnapshot.active_saved_draft
    delete builderSnapshot.saved_id
    delete builderSnapshot.saved_updated_at
  }
  var payload = {
    blank_recovery: (this.draft.builder.blank_recovery || []).filter(row => row.status !== 'resolved'),
    po_number: this.draft.builder.po_number || '',
    wait_on_customer_po: this.draft.builder.wait_on_customer_po === true || this.draft.builder.wait_on_customer_po === 1 || this.draft.builder.wait_on_customer_po === '1' ? 1 : 0,
    customer_info: this.draft.builder.customer_info || '',
    ship_to_info: this.draft.builder.ship_to_info || '',
    new_ship_to: this.draft.builder.new_ship_to ? 1 : 0,
    new_ship_info: this.draft.builder.new_ship_info || {},
    builder_snapshot: builderSnapshot,
    rep: this.draft.builder.rep || '',
    description: '',
    event_order: this.draft.order.event_order ? 1 : 0,
    in_hands_date: this.draft.order.in_hands_date || '',
    future_ship_date_boolean: this.draft.order.future_ship_date_boolean ? 1 : 0,
    future_ship_date: this.draft.order.future_ship_date || '',
    prebook: clone2(this.normalizePrebookOption2(this.draft.order.prebook)),
    fast_lane: this.draft.order.fast_lane ? 1 : 0,
    available_emails: this.draft.order.available_emails || [],
    extra_emails: this.draft.order.extra_emails || '',
    email_text: this.draft.order.email_text || '',
    selected_emails: checkedEmails,
    order_items: evaluatedOrderItems,
    groups: this.draft.groups.map(function (group) {
      var preview = this.previewFormForGroup2(group)
      return {
        group_id: group.id,
        name: group.name,
        ship_date: group.ship_date || '',
        event_date: this.draft.order.in_hands_date || '',
        in_hands_date: this.draft.order.in_hands_date || '',
        program_info: group.program_info || '',
        ship_via: group.ship_via || '',
        wait_on_customer: group.wait_on_customer ? 1 : 0,
        special_instructions: group.special_instructions || '',
        rush: group.rush ? 1 : 0,
        ribbon: group.ribbon ? 1 : 0,
        hangtags: group.hangtags ? 1 : 0,
        string_tags: group.string_tags ? 1 : 0,
        fast_lane: group.fast_lane ? 1 : 0,
        uploads: group.uploads || [],
        additional_charges: clone2(group.additional_charges || []),
        royalty_charges: clone2(group.royalty_charges || []),
        selected_promotion: group.selected_promotion || null,
        tier: group.tier || '',
        one_to_23_upcharge_cost: preview.one_to_23_upcharge_cost,
        one_to_23_upcharge_pieces: preview.one_to_23_upcharge_pieces,
        one_to_23_upcharge_price: preview.one_to_23_upcharge_price,
        order_items: clone2(preview.order_items || []),
        items: clone2(group.items || [])
      }
    }, this),
    builder_version: 'create_order2_group_review'
  }
  Object.assign(payload, savedFields)
  if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined' && console.debug) {
    console.debug('[saved-draft-submit]', {
      route: this.$route && this.$route.path || '',
      active_saved_id: (savedDraftIdentity2(this.draft.builder) || {}).saved_id || '',
      handoff_saved_id: this.draft.builder.saved_id || '',
      payload_saved_id: payload.saved_id || '',
      payload_has_saved_version: !!payload.saved_updated_at
    })
  }
  return payload
}

methods.placementDiagnosticsEnabled2 = function () {
  var user = this.$store && this.$store.state ? this.$store.state.user : null
  return process.env.NODE_ENV !== 'production' && !!user && ['admin', 'csr_admin', 'developer'].indexOf(user.user_type) !== -1
}

methods.placementDiagnosticsOpen2 = function (group) {
  return !!this.placementDiagnosticState2[group ? group.id : '']
}

methods.togglePlacementDiagnostics2 = function (group) {
  if (!group) return
  this.$set(this.placementDiagnosticState2, group.id, !this.placementDiagnosticState2[group.id])
}

methods.diagnosticValue2 = function (value) {
  return value === null || value === undefined || value === '' ? 'Missing' : String(value)
}

methods.placementDiagnosticRows2 = function (group, groupIndex) {
  if (!this.placementDiagnosticsEnabled2() || !group || !this.draft) return []
  var submitPreview = this.buildSubmitPreview2()
  var submissionGroup = (submitPreview.groups || []).find(function (candidate) { return candidate.group_id === group.id }) || null
  var pricingPayload = buildOrderQuoteV2Payload(submitPreview)
  var pricingGroup = (pricingPayload.groups || []).find(function (candidate) { return candidate.id === group.id }) || null
  var evaluatorGroup = this.quoteGroupFor2(group)
  return buildPlacementDiagnostics({
    group: group,
    groupIndex: groupIndex,
    builder: this.draft.builder || {},
    submissionGroup: submissionGroup,
    pricingGroup: pricingGroup,
    evaluatorGroup: evaluatorGroup,
    pdfForm: this.previewFormForGroup2(group)
  })
}

methods.validProductClassSpecificGroup2 = function (group, groupIndex) {
  var rows = this.placementDiagnosticRows2(group, groupIndex)
  return validProductClassSpecificDifference(rows.reduce(function (all, row) {
    return all.concat(row.decorations.map(function (decoration) {
      return { targetProductClass: row.targetProductClass, submission: decoration.submission }
    }))
  }, []))
}

export default {
  mixins: [EventScheduling],
  name: 'OrderGroups2',
  components: {
    EventSchedule2,
    PageBootstrap2,
    PdfView,
    AuthoritativePdfReview,
    AdditionalChargesTable,
    RoyaltiesTable,
    PromotionPopup
  },
  data () {
    return {
      activeGroupId2: '',
      activeGroupPosition2: 0,
      groupPanelState2: {},
      placementDiagnosticState2: {},
      pdfPreviewState2: {},
      pdfReview2: { open: false, index: 0, groupId: '', fingerprint: '', viewed: {}, stale: false },
      missingDraft: false,
      pageBootstrapState2: 'initializing',
      pageBootstrapReference2: '',
      pageBootstrapGeneration2: 0,
      submitInProgress: false,
      draft: null,
      quoteResult: null,
      quoteUpdating: false,
      quoteStale: false,
      quoteError: '',
      quoteDiagnostics: false,
      quoteRequestSequence2: 0,
      quotePendingFingerprint2: '',
      quoteSuccessfulFingerprint2: '',
      quoteRefreshTimer2: null,
      quoteCancelSource2: null,
      quoteHydrating: true,
      quoteInitialScheduled2: false,
      timingFastLaneConfirmed2: false,
      shipDateUpdating2: false,
      shipDateError2: '',
      shipDateRequestGeneration2: 0,
      shipDateRefreshTimer2: null,
      shipViaOptions: [
        { ship_via_code: 'BS', ship_via_text: 'BEST WAY' },
        { ship_via_code: 'CP', ship_via_text: 'CALL IN-HOUSE CSR FOR SHIPPING INFORMATION' },
        { ship_via_code: 'CS', ship_via_text: 'CALL CUSTOMER FOR ROUTING' },
        { ship_via_code: 'CU', ship_via_text: 'CUST. PICK UP' },
        { ship_via_code: 'HR', ship_via_text: 'HOLD FOR RELEASE' },
        { ship_via_code: 'IH', ship_via_text: 'IN HOUSE DELIVERY' },
        { ship_via_code: 'OC', ship_via_text: 'OCEAN' },
        { ship_via_code: 'PM', ship_via_text: 'USPS PRIORITY 1-3 DAYS' },
        { ship_via_code: 'RG', ship_via_text: 'SEE ROUTING GUIDE FOR INSTRUCTIONS' },
        { ship_via_code: 'TK', ship_via_text: 'TRUCK' },
        { ship_via_code: 'ZA', ship_via_text: 'DIRECT AIR PAI FF' },
        { ship_via_code: 'IA', ship_via_text: 'IMPERIAL HAWAII FREIGHT' },
        { ship_via_code: 'IG', ship_via_text: 'IMPERIAL HAWAII UPS GROUND' },
        { ship_via_code: 'QA', ship_via_text: 'FDX GRD PREPAID' },
        { ship_via_code: 'QB', ship_via_text: 'FDX GRD BILL RECIPIENT' },
        { ship_via_code: 'QC', ship_via_text: 'FDX GRD 3RD PARTY' },
        { ship_via_code: 'QN', ship_via_text: 'FDX 2D PREPAID' },
        { ship_via_code: 'QO', ship_via_text: 'FDX 2D BILL RECIPIENT' },
        { ship_via_code: 'QP', ship_via_text: 'FDX 2D 3RD PARTY' },
        { ship_via_code: 'QT', ship_via_text: 'FDX 3D PREPAID' },
        { ship_via_code: 'QU', ship_via_text: 'FDX 3D BILL RECIPIENT' },
        { ship_via_code: 'QV', ship_via_text: 'FDX 3D 3RD PARTY' },
        { ship_via_code: '21', ship_via_text: 'FDX 1ST OVNT PREPAID' },
        { ship_via_code: '22', ship_via_text: 'FDX 1ST OVNT BILL RECIPIENT' },
        { ship_via_code: '23', ship_via_text: 'FDX 1ST OVNT 3RD PARTY' },
        { ship_via_code: '31', ship_via_text: 'FDX PRTY OVNT PREPAID' },
        { ship_via_code: '32', ship_via_text: 'FDX PRTY OVNT BILL RECIPIENT' },
        { ship_via_code: '33', ship_via_text: 'FDX PRTY OVNT 3RD PARTY' },
        { ship_via_code: '41', ship_via_text: 'FDX STD OVNT PREPAID' },
        { ship_via_code: '42', ship_via_text: 'FDX STD OVNT BILL RECIPIENT' },
        { ship_via_code: '43', ship_via_text: 'FDX STD OVNT 3RD PARTY' },
        { ship_via_code: '91', ship_via_text: 'FDX INTL ECON PREPAID' },
        { ship_via_code: '92', ship_via_text: 'FDX INTL ECON BILL RECIPIENT' },
        { ship_via_code: '93', ship_via_text: 'FDX INTL ECON 3RD PARTY' },
        { ship_via_code: 'HH', ship_via_text: 'IMP HI FRT 2016 (.75)' },
        { ship_via_code: 'SI', ship_via_text: '*** SEE SHIPPING INSTRUCTIONS ***' },
        { ship_via_code: '3A', ship_via_text: 'UPS NEXT DAY AIR SAVER PREPAID FRT' },
        { ship_via_code: '1B', ship_via_text: 'UPS GROUND FRT COLLECT' },
        { ship_via_code: '2A', ship_via_text: 'UPS NEXT DAY AIR PREPAID FRT' },
        { ship_via_code: '2B', ship_via_text: 'UPS NEXT DAY AIR FRT COLLECT' },
        { ship_via_code: '9A', ship_via_text: 'UPS NEXT DAY AIR EARLY AM PREPAID FRT' },
        { ship_via_code: '2H', ship_via_text: 'UPS NEXT DAY AIR NO CHARGE' },
        { ship_via_code: '1H', ship_via_text: 'UPS GROUND NO CHARGE' },
        { ship_via_code: '1A', ship_via_text: 'UPS GROUND PREPAID FRT' },
        { ship_via_code: '4A', ship_via_text: 'UPS 2ND DAY AIR PREPAID FRT' },
        { ship_via_code: '4H', ship_via_text: 'UPS 2ND DAY NO CHARGE' },
        { ship_via_code: '6A', ship_via_text: 'UPS 3 DAY SELECT PREPAID FRT' },
        { ship_via_code: '4B', ship_via_text: 'UPS 2ND DAY AIR FRT COLLECT' },
        { ship_via_code: '6B', ship_via_text: 'UPS 3 DAY SELECT FRT COLLECT' },
        { ship_via_code: '1F', ship_via_text: 'UPS GROUND 3RD PARTY' },
        { ship_via_code: '2F', ship_via_text: 'UPS NEXT DAY AIR 3RD PARTY' },
        { ship_via_code: '5A', ship_via_text: 'UPS 2ND DAY AIR AM PREPAID FRT' },
        { ship_via_code: '2J', ship_via_text: 'UPS NXT DAY (CHARGE GROUND)' },
        { ship_via_code: '2K', ship_via_text: 'UPS 2ND DAY (CHARGE GROUND)' },
        { ship_via_code: '2L', ship_via_text: 'UPS NXT DAY SAVER (CHARGE GROUND)' },
        { ship_via_code: '6H', ship_via_text: 'UPS 3 DAY SELECT NO CHARGE' },
        { ship_via_code: '3F', ship_via_text: 'UPS NEXT DAY AIR SAVER 3RD PARTY' },
        { ship_via_code: '3H', ship_via_text: 'UPS NEXT DAY AIR SAVER NO CHARGE' },
        { ship_via_code: '4F', ship_via_text: 'UPS 2ND DAY AIR 3RD PARTY' },
        { ship_via_code: '5F', ship_via_text: 'UPS 2ND DAY AIR AM 3RD PARTY' },
        { ship_via_code: '5G', ship_via_text: 'UPS 2ND DAY AIR EARLY A.M. NO CHARGE.' },
        { ship_via_code: '6F', ship_via_text: 'UPS 3 DAY SELECT 3RD PARTY' },
        { ship_via_code: '7A', ship_via_text: 'RESIDENTIAL GROUND PREPAID FRT' },
        { ship_via_code: '7H', ship_via_text: 'RESIDENTIAL 2ND DAY FRT COLLECT' },
        { ship_via_code: '9F', ship_via_text: 'UPS NEXT DAY AIR EARLY AM 3RD PARTY' },
        { ship_via_code: '9H', ship_via_text: 'UPS NXT DAY EARY AM NO CHARGE' },
        { ship_via_code: '3B', ship_via_text: 'UPS NEXT DAY AIR SAVER FRT COLLECT' },
        { ship_via_code: '5B', ship_via_text: 'UPS 2ND DAY AIR AM FRT COLLECT' },
        { ship_via_code: '9B', ship_via_text: 'UPS NEXT DAY AIR EARLY AM FRT COLLECT' },
        { ship_via_code: 'HK', ship_via_text: 'IMP HI FRT 1.75' },
        { ship_via_code: 'HM', ship_via_text: 'IMP HI FRT 2.00' },
        { ship_via_code: 'HN', ship_via_text: 'IMP HI FRT 1.00' },
        { ship_via_code: 'HJ', ship_via_text: 'IMP HI FRT TRN2016 (.50)' }
      ],
      preBookOptions: [],
      prebookCatalogLoaded2: false,
      prebookCatalogError2: false,
      prebookNotice2: '',
      reviewAttempted2: false,
      shipViaOptionsReady2: true,
      dropzoneOptions: {
        url: 'test',
        autoProcessQueue: false,
        thumbnailWidth: 150,
        addRemoveLinks: true,
        maxFilesize: 10.0,
        headers: { 'Upload File(s)': '' }
      },
      draftPersistenceWarning2: '',
      draftPersistenceResult2: null,
      storageInventory2: []
    }
  },
  computed: {
    pageBootstrapMessage2 () {
      return this.pageBootstrapState2 === 'redirecting' ? 'Returning to the Builder…' : 'Preparing Order Groups…'
    },
    activeTimingSummary2 () {
      if (!this.draft || !this.draft.builder || !this.draft.builder.scheduling_intent) return false
      const timing = this.draft.builder.scheduling_intent
      return !!(timing.event_order || timing.future_ship_date_boolean || this.timingFastLaneConfirmed2 || (this.draft.order && this.draft.order.prebook) || this.prebookEligible2())
    },
    serializedSubmitPreview2 () {
      return JSON.stringify(this.buildSubmitPreview2(), null, 2)
    }
  },
  watch: {
    'draft.builder.scheduling_intent': { deep: true, handler () {
      this.syncBuilderTiming2()
      if (!this.quoteHydrating) this.authoritativeQuoteInputChanged2()
    } },
    'draft.order.fast_lane': function () {
      this.syncFastLaneAcrossGroups2()
      this.syncPromotionVisibility2()
      this.queueRefreshGroupEvaluations2()
    },
    'draft.order.prebook': function () {
      this.syncPromotionVisibility2()
    },
    draft: {
      deep: true,
      handler () {
        this.ensureActiveGroup2()
        this.queuePersistDraft2()
        this.invalidatePdfReview2()
      }
    },
    quoteSuccessfulFingerprint2 () { this.invalidatePdfReview2() },
    eventResultKey2 () { this.invalidatePdfReview2() }
  },
  mounted () {
    this.initializeQuoteFlow2()
  },
  beforeDestroy () {
    this.pageBootstrapGeneration2 += 1
    this.quoteRequestSequence2 += 1
    this.cancelQuoteRequest2()
    if (this.quoteRefreshTimer2) {
      clearTimeout(this.quoteRefreshTimer2)
      this.quoteRefreshTimer2 = null
    }
    if (this.persistDraftTimer2) {
      clearTimeout(this.persistDraftTimer2)
      this.persistDraftTimer2 = null
    }
    if (this.shipDateRefreshTimer2) clearTimeout(this.shipDateRefreshTimer2)
    if (this.refreshGroupEvaluationsTimer2) {
      clearTimeout(this.refreshGroupEvaluationsTimer2)
      this.refreshGroupEvaluationsTimer2 = null
    }
    window.removeEventListener('beforeunload', this.persistDraft2)
  },
  methods: methods
}
</script>

<style scoped>
.order-groups2-page {
  padding: 24px;
  min-height: calc(100vh - 120px);
  background:
    radial-gradient(circle at top right, rgba(29, 155, 240, 0.08), transparent 28%),
    linear-gradient(180deg, #f7f8fb 0%, #eef2f7 100%);
}

.start-order-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 16px;
}
.timing-review2 { margin: 16px 0; padding: 12px 14px; border: 1px solid #dce5ee; border-radius: 8px; background: #f8fafc; }
.timing-review2-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 6px; }

.page-hero {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 18px;
  padding: 22px 24px;
  border-radius: 18px;
  background: linear-gradient(135deg, #ffffff 0%, #edf5fc 100%);
  border: 1px solid #dbe7f3;
}

.eyebrow {
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #57799b;
  font-weight: 700;
  margin-bottom: 8px;
}

.page-hero h1 {
  margin: 0 0 8px;
  font-size: 32px;
  line-height: 1.1;
  color: #23384d;
}

.page-hero p {
  margin: 0;
  max-width: 700px;
  color: #60788f;
}

.bottom-submit-bar {
  position: sticky;
  bottom: 0;
  z-index: 20;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding: 18px 20px;
  border-radius: 16px 16px 0 0;
  background: #ffffff;
  border: 1px solid #dbe7f3;
  box-shadow: 0 -6px 18px rgba(31, 55, 79, 0.09);
}

.bottom-submit-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.authoritative-quote-card {
  border-left: 4px solid #321fdb;
}

.quote-status {
  align-self: center;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 10px;
}

.quote-status.current { background: #d8f3e5; color: #17683a; }
.quote-status.updating { background: #e6ecff; color: #243fa3; }
.quote-status.stale { background: #fff0cf; color: #805900; }

.quoted-order-total {
  align-items: baseline;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.quoted-order-total strong {
  color: #321fdb;
  font-size: 1.75rem;
}

.quote-error {
  align-items: center;
  color: #a52222;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}

.quote-breakdown > div {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}

.quote-breakdown-total {
  border-top: 1px solid #d8dbe0;
  margin-top: 6px;
  padding-top: 9px !important;
}

.promotion-adjustment,
.promotion-benefit {
  color: #17683a;
}

.promotion-guidance-row {
  border-bottom: 1px solid #e4e7ea;
  padding: 10px 0;
}

.promotion-guidance-row:last-child { border-bottom: 0; }

.quote-diagnostic,
.quote-contract-diagnostics {
  background: #f2f4f8;
  color: #4f5d73;
  font-family: monospace;
  font-size: 0.75rem;
  margin-top: 6px;
  overflow-x: auto;
  padding: 8px;
}

.summary-block {
  padding: 12px 14px;
  border-radius: 12px;
  background: #f7fbff;
  border: 1px solid #dce8f3;
  color: #36506a;
}

.warning-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.warning-line {
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff4e5;
  border: 1px solid #f1d4a2;
  color: #83561b;
  font-size: 13px;
}

.field-label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #5f7891;
}

.toggle-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-weight: 600;
  color: #29445d;
}

.email-row {
  margin-bottom: 14px;
}

.review-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.review-stat {
  padding: 14px;
  border-radius: 12px;
  background: #eff5fb;
  border: 1px solid #dce7f2;
}

.review-stat-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #73879c;
}

.review-stat-value {
  font-size: 24px;
  font-weight: 800;
  color: #20374d;
}

.uploads-card {
  clear: both;
  width: 100%;
}

.uploads-card >>> .card-body {
  clear: both;
}

.upload-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.upload-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #dce7f2;
  border-radius: 10px;
  background: #f8fbff;
}

.upload-meta {
  color: #36506a;
  overflow-wrap: anywhere;
}

.group-card {
  border: 1px solid #dfe7ef;
}

.group-navigator {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 2px 2px 8px;
}

.group-navigator-item {
  display: flex;
  flex: 0 0 290px;
  flex-direction: column;
  gap: 3px;
  padding: 12px 14px;
  border: 1px solid #d7e1eb;
  border-radius: 12px;
  background: #fff;
  color: #5c7287;
  text-align: left;
  cursor: pointer;
  outline: none;
}

.group-navigator-item:focus {
  box-shadow: 0 0 0 3px rgba(51, 153, 255, 0.18);
}

.group-navigator-item.active {
  border-color: #3399ff;
  box-shadow: 0 0 0 3px rgba(51, 153, 255, 0.13);
}

.group-navigator-item.ready:not(.active) {
  border-color: #8bc3a3;
}

.group-navigator-number {
  color: #23384d;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.group-navigator-status {
  align-self: flex-start;
  padding: 3px 8px;
  border-radius: 999px;
  background: #fff0db;
  color: #925700;
  font-size: 11px;
  font-weight: 800;
}

.group-navigator-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 3px;
}

.group-nav-chip {
  max-width: 100%;
  overflow: hidden;
  padding: 3px 7px;
  border-radius: 999px;
  background: #e8f2fb;
  color: #245276;
  font-size: 10px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-nav-chip.decoration {
  background: #f1f3f6;
  color: #5d6d7c;
}

.group-navigator-counts {
  color: #657b90;
  font-size: 11px;
}

.group-fix-action {
  align-self: flex-start;
  margin-top: 4px;
  padding: 4px 8px;
  border: 1px solid #e2b55e;
  border-radius: 7px;
  background: #fff8e8;
  color: #805900;
  font-size: 11px;
  font-weight: 800;
}

.issue-target-highlight {
  animation: issueTargetPulse 2.4s ease-out;
  outline: 3px solid rgba(255, 153, 0, 0.55);
  outline-offset: 3px;
}

@keyframes issueTargetPulse {
  from { outline-color: rgba(255, 153, 0, 0.9); }
  to { outline-color: rgba(255, 153, 0, 0); }
}

.group-navigator-item.ready .group-navigator-status,
.group-navigator-status.ready {
  background: #dff1e7;
  color: #277348;
}

.group-collapsible-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
}

.group-collapsible-header:focus {
  box-shadow: inset 0 0 0 2px #3399ff;
  outline: none;
}

.group-panel-summary {
  display: block;
  margin-top: 3px;
  color: #74879a;
  font-size: 11px;
  font-weight: 500;
}

.embedded-charge-table >>> .card-body {
  padding: 0;
}

.group-final-back {
  margin-right: auto;
}

.group-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.section-kicker {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #7890a8;
  margin-bottom: 4px;
}

.group-title {
  font-size: 22px;
  font-weight: 800;
  color: #23384d;
}

.group-subtitle {
  margin-top: 4px;
  color: #678097;
  font-size: 13px;
}

.detail-heading {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #5f7891;
  font-weight: 700;
  margin-bottom: 8px;
}

.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #e7f1fb;
  color: #21486e;
  font-size: 12px;
  font-weight: 600;
}

.program-options {
  margin-top: 14px;
}

.toggle-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.not-ported-box {
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff8e8;
  border: 1px solid #f0dfae;
  color: #6f5a22;
}

.pdf-preview-block {
  margin-top: 16px;
}

.pdf-preview-wrap {
  margin-top: 12px;
  padding: 12px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #dfe7ef;
  overflow: auto;
}

.placement-diagnostic-card {
  border: 1px solid #d7a93e;
  background: #fffdf4;
}

.placement-diagnostic-body {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11px;
  line-height: 1.5;
}

.placement-diagnostic-item {
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid #e5d9ad;
  border-radius: 6px;
  background: #fff;
}

.placement-diagnostic-heading {
  font-weight: 700;
}

.placement-diagnostic-decoration {
  padding: 9px;
  margin-top: 8px;
  border-left: 4px solid #3a9b65;
  background: #f3fbf6;
}

.placement-diagnostic-decoration.diagnostic-mismatch {
  border-left-color: #c25b3c;
  background: #fff3ef;
}

.pdf-review-shell2 {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
}

::v-deep .pdf-review-modal2 .modal-dialog {
  height: 92vh;
  height: 92dvh;
  margin-top: 4vh;
  margin-bottom: 4vh;
  overflow: hidden;
}

::v-deep .pdf-review-modal-content2 {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border-radius: 10px;
}

::v-deep .pdf-review-modal-content2 .modal-header,
::v-deep .pdf-review-modal-content2 .modal-footer {
  flex: 0 0 auto;
}

::v-deep .pdf-review-modal-content2 .modal-body {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

::v-deep .pdf-review-modal-content2 .modal-footer {
  flex-wrap: wrap;
}

.pdf-review-header2 {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.pdf-review-tabs2 {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.pdf-review-tabs2 button {
  min-width: 40px;
  padding: 6px 12px;
  border: 1px solid #9da5b1;
  border-radius: 6px;
  background: #fff;
}

.pdf-review-tabs2 button.active {
  border-color: #2874c6;
  background: #2874c6;
  color: #fff;
}

.pdf-review-tabs2 button.reviewed:not(.active) {
  border-color: #2f9e64;
  background: #edf9f2;
}

.pdf-review-tabs2 button:focus-visible {
  outline: 3px solid rgba(40, 116, 198, 0.35);
  outline-offset: 2px;
}

.pdf-review-preview2 {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  background: #fff;
}

.placement-diagnostic-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  margin-top: 7px;
}

@media (max-width: 991px) {
  .page-hero,
  .group-header {
    display: block;
  }

  .hero-actions {
    margin-top: 14px;
  }

  .review-stats,
  .toggle-grid {
    grid-template-columns: 1fr;
  }

  .bottom-submit-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .bottom-submit-actions,
  .bottom-submit-actions .btn,
  .group-final-back,
  .group-final-back .btn {
    width: 100%;
  }

  .bottom-submit-actions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 575px) {
  .order-groups2-page {
    padding: 14px;
  }

  .group-navigator-item {
    flex-basis: 250px;
  }

  .bottom-submit-actions {
    grid-template-columns: 1fr;
  }

  .group-issue-action {
    width: 100%;
  }

  .pdf-review-header2 {
    display: block;
  }

  .pdf-review-preview2 {
    max-width: 100%;
  }

  ::v-deep .pdf-review-modal2 .modal-dialog {
    width: 100%;
    max-width: 100%;
    height: 100vh;
    height: 100dvh;
    margin: 0;
  }

  ::v-deep .pdf-review-modal-content2 {
    border-radius: 0;
  }

  ::v-deep .pdf-review-modal-content2 .modal-footer > * {
    flex: 1 1 calc(50% - 8px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .issue-target-highlight {
    animation: none;
  }
}
</style>

<style src="./compact_order_toggle2.css"></style>
